import { signal, effect } from '@preact/signals-core';
import {
  activeRoleSignal,
  getAuthHeaders,
  allCustomersSignal,
  searchCustomersForAutocomplete,
  searchModelsForAutocomplete,
  sitesSignal,
  assetsListSignal,
  initAssetsSignals,
  deviceModelsSignal,
  pageTypesSignal
} from './assets';
import { userProfileSignal } from '../store/auth';
import { consumablesSignal, refreshConsumables } from './device_models';
import { escapeHtml } from '../utils/sanitize';
import { components } from '../api/v1';
import { formatDate, formatDateTime, formatDurationWords } from '../utils/formatters';
import { showToast } from '../utils/toast';

type Ticket = components['schemas']['Ticket'];
type User = components['schemas']['User'];
type ServiceSession = components['schemas']['ServiceSession'];
type TicketTimeline = components['schemas']['TicketTimeline'];

// ─── Signals ───────────────────────────────────────────────────────────────────

export const ticketsListSignal = signal<Ticket[]>([]);
export const selectedTicketIdSignal = signal<string | null>(null);
export const ticketsStatusFilterSignal = signal<string>('open');
export const ticketsLoadingSignal = signal<boolean>(false);
export const ticketsPageSignal = signal<number>(1);
export const ticketsPageSizeSignal = signal<number>(15);
export const ticketsTotalCountSignal = signal<number>(0);
export const ticketsStatusCountsSignal = signal<Record<string, number>>({
  all: 0,
  open: 0,
  assigned: 0,
  in_progress: 0,
  resolved: 0,
  closed: 0,
});
export const ticketUsersMapSignal = signal<Map<string, User>>(new Map());
export const ticketAssignmentsSignal = signal<Map<string, string[]>>(new Map());
export const technicianWorkloadMapSignal = signal<Map<string, number>>(new Map());
export const assignableUsersLoadErrorSignal = signal<string | null>(null);
export const ticketSessionsSignal = signal<ServiceSession[]>([]);
export const ticketTimelineEventsSignal = signal<TicketTimeline[]>([]);
export const ticketsSearchQuerySignal = signal<string>('');
export const ticketSessionsLoadingSignal = signal<boolean>(false);

// Tracks whether the local reactive effect has been set up for this page mount
let _ticketsEffectDisposer: (() => void) | null = null;
let _ticketsPollInterval: any = null;
let _ticketsKeyDownHandler: ((e: KeyboardEvent) => void) | null = null;

// Captured scroll position for state restoration (updated before unmount)
let _ticketsScrollY = 0;

// Intake form state (local, non-reactive variables to prevent full-page innerHTML rebuilds during modal usage)
let intakeSelectedCustomerId: string | null = null;
let intakeSelectedSiteId: string | null = null;
let intakeSelectedModelId: string | null = null;
let intakeIsNewCustomer = false;
let intakeIsNewSite = false;
let intakeMatchedAssetId: string | null = null;
let intakeSelectedAssetId: string | null = null;
let editSelectedCustomerId: string | null = null;
let editSelectedSiteId: string | null = null;
let editSelectedAssetId: string | null = null;
let isPrepopulatingIntakeAsset = false;
let isPrepopulatingEditAsset = false;
export const intakeSubmittingSignal = signal<boolean>(false);

// ─── Visits/Sessions Fetching ──────────────────────────────────────────────────

type ServiceSessionWithParts = ServiceSession & { parts?: components['schemas']['PartRecommendation'][] };
const TICKET_STATUS_FILTERS = ['open', 'assigned', 'in_progress', 'resolved', 'closed'] as const;
type TicketStatusFilterKey = 'all' | typeof TICKET_STATUS_FILTERS[number];

// Guard: tracks which ticketId is currently being fetched so duplicate concurrent calls are dropped
let _fetchingSessionsForTicketId: string | null = null;

export async function fetchTicketSessions(ticketId: string) {
  // Deduplicate: if a fetch is already in-flight for this exact ticket, do nothing
  if (_fetchingSessionsForTicketId === ticketId) return;
  _fetchingSessionsForTicketId = ticketId;
  ticketSessionsLoadingSignal.value = true;
  reRenderDetailPanel();

  try {
    const h = getAuthHeaders();

    // Ensure pageTypes are loaded
    if ((pageTypesSignal.peek() || []).length === 0) {
      try {
        const ptRes = await fetch('/api/v1/page-types', { headers: h });
        if (ptRes.ok) {
          pageTypesSignal.value = await ptRes.json();
        }
      } catch (e) {
        console.error('Failed to fetch page types', e);
      }
    }

    const ticket = getSelectedTicket();

    // Fetch sessions, timeline, and asset readings (if ticket has assetId) in parallel
    const fetchPromises: Promise<any>[] = [
      fetch(`/api/v1/tickets/${ticketId}/sessions`, { headers: h }),
      fetch(`/api/v1/tickets/${ticketId}/timeline`, { headers: h }),
    ];
    if (ticket && ticket.assetId) {
      fetchPromises.push(fetch(`/api/v1/assets/${ticket.assetId}/readings`, { headers: h }));
    }

    const results = await Promise.all(fetchPromises);
    const sessionsRes = results[0];
    const timelineRes = results[1];
    const readingsRes = results[2];

    const readingsMap = new Map<string, Record<string, number>>();
    if (readingsRes && readingsRes.ok) {
      try {
        const readingsList: any[] = await readingsRes.json();
        if (Array.isArray(readingsList)) {
          readingsList.forEach(r => {
            if (r.id && r.counters) {
              readingsMap.set(r.id, r.counters);
            }
          });
        }
      } catch (e) {
        console.error('Failed to parse asset readings', e);
      }
    }

    // Process sessions + parts + reading counters
    if (sessionsRes.ok) {
      const sessions = await sessionsRes.json() as ServiceSession[];
      const sessionsWithParts = await Promise.all(sessions.map(async (sess: ServiceSessionWithParts) => {
        try {
          const partsRes = await fetch(`/api/v1/parts-recommendations?sessionId=${sess.id}`, {
            headers: h,
          });
          if (partsRes.ok) {
            sess.parts = await partsRes.json();
          } else {
            sess.parts = [];
          }
        } catch (e) {
          console.error(`Failed to fetch parts for session ${sess.id}`, e);
          sess.parts = [];
        }

        // Attach counters from readingsMap if readingId exists
        if (sess.readingId) {
          if (readingsMap.has(sess.readingId)) {
            (sess as any).counters = readingsMap.get(sess.readingId);
          } else {
            try {
              const singleReadingRes = await fetch(`/api/v1/readings/${sess.readingId}`, { headers: h });
              if (singleReadingRes.ok) {
                const singleReading = await singleReadingRes.json();
                if (singleReading && singleReading.counters) {
                  (sess as any).counters = singleReading.counters;
                }
              }
            } catch (e) {
              // ignore
            }
          }
        }

        return sess;
      }));
      ticketSessionsSignal.value = sessionsWithParts;
    }

    // Process timeline events
    if (timelineRes.ok) {
      const timeline = await timelineRes.json();
      ticketTimelineEventsSignal.value = timeline.events || [];
    } else {
      ticketTimelineEventsSignal.value = [];
    }

    ticketSessionsLoadingSignal.value = false;
    reRenderDetailPanel();
  } catch (err) {
    console.error('Failed to fetch ticket sessions/timeline', err);
    ticketSessionsLoadingSignal.value = false;
  } finally {
    // Always release the guard, even if the ticketId changed while we were fetching
    if (_fetchingSessionsForTicketId === ticketId) {
      _fetchingSessionsForTicketId = null;
    }
  }
}


// ─── Data Fetching ─────────────────────────────────────────────────────────────

async function fetchTicketCount(headers: Record<string, string>, status: TicketStatusFilterKey, searchQuery: string): Promise<number> {
  const params = new URLSearchParams({
    page: '1',
    pageSize: '1',
  });
  if (searchQuery) {
    params.set('search', searchQuery);
  }
  if (status !== 'all') {
    params.set('status', status);
  }

  const res = await fetch(`/api/v1/tickets?${params.toString()}`, { headers });
  if (!res.ok) return 0;
  return Number(res.headers.get('X-Total-Count') || 0);
}

async function refreshTicketStatusCounts(headers: Record<string, string>, searchQuery: string): Promise<void> {
  const entries = await Promise.all(
    (['all', ...TICKET_STATUS_FILTERS] as TicketStatusFilterKey[]).map(
      async (status) => [status, await fetchTicketCount(headers, status, searchQuery)] as const,
    ),
  );
  ticketsStatusCountsSignal.value = Object.fromEntries(entries) as Record<TicketStatusFilterKey, number>;
}

function parseUsersPayload(payload: unknown): User[] | null {
  if (Array.isArray(payload)) {
    return payload as User[];
  }

  if (payload && typeof payload === 'object' && 'items' in payload) {
    const items = (payload as { items?: unknown }).items;
    if (Array.isArray(items)) {
      return items as User[];
    }
  }

  return null;
}

async function fetchUsersList(url: string, headers: Record<string, string>): Promise<User[] | null> {
  const res = await fetch(url, { headers });
  if (!res.ok) {
    return null;
  }

  const payload = await res.json() as unknown;
  return parseUsersPayload(payload);
}

function getTechnicianUsers(users: User[]): User[] {
  return users.filter((user) => user.role === 'technician');
}

function replaceTechnicianUsers(technicians: User[]): void {
  const nextMap = new Map<string, User>();
  technicians.forEach((user) => {
    nextMap.set(user.id, user);
  });
  ticketUsersMapSignal.value = nextMap;
}

export function canManageTicketAssignments(): boolean {
  const role = userProfileSignal.peek()?.role ?? activeRoleSignal.peek();
  return role === 'superadmin' || role === 'admin' || role === 'manager';
}

async function refreshAssignableUsers(headers: Record<string, string>): Promise<void> {
  assignableUsersLoadErrorSignal.value = null;

  if (!canManageTicketAssignments()) {
    ticketUsersMapSignal.value = new Map();
    technicianWorkloadMapSignal.value = new Map();
    return;
  }

  try {
    const filteredUsers = await fetchUsersList('/api/v1/users?limit=1000&role=technician', headers);
    const filteredTechnicians = filteredUsers ? getTechnicianUsers(filteredUsers) : [];
    if (filteredTechnicians.length > 0) {
      replaceTechnicianUsers(filteredTechnicians);
      return;
    }

    const allUsers = await fetchUsersList('/api/v1/users?limit=1000', headers);
    if (allUsers) {
      const allTechnicians = getTechnicianUsers(allUsers);
      if (allTechnicians.length > 0) {
        replaceTechnicianUsers(allTechnicians);
        return;
      }

      if (ticketUsersMapSignal.peek().size === 0) {
        assignableUsersLoadErrorSignal.value = 'No technician accounts were returned by the users API.';
      }
      return;
    }

    if (ticketUsersMapSignal.peek().size === 0) {
      assignableUsersLoadErrorSignal.value = 'Unable to load technician accounts.';
    }
  } catch (err) {
    console.error('Failed to refresh assignable users', err);
    if (ticketUsersMapSignal.peek().size === 0) {
      assignableUsersLoadErrorSignal.value = 'Unable to load technician accounts.';
    }
  }
}

async function safeRefreshTechnicianWorkloads(headers: Record<string, string>): Promise<void> {
  try {
    await refreshTechnicianWorkloads(headers);
  } catch (err) {
    console.error('Failed to refresh technician workloads', err);
    technicianWorkloadMapSignal.value = new Map();
    return;
  }
}

async function fetchTicketsByStatus(
  headers: Record<string, string>,
  status: Exclude<TicketStatusFilterKey, 'all'>,
): Promise<Ticket[]> {
  const pageSize = 250;
  const firstRes = await fetch(
    `/api/v1/tickets?status=${encodeURIComponent(status)}&page=1&pageSize=${pageSize}`,
    { headers },
  );
  if (!firstRes.ok) {
    return [];
  }

  const firstPage = await firstRes.json() as Ticket[];
  const totalCount = Number(firstRes.headers.get('X-Total-Count') || firstPage.length || 0);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  if (totalPages === 1) {
    return firstPage;
  }

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      fetch(
        `/api/v1/tickets?status=${encodeURIComponent(status)}&page=${index + 2}&pageSize=${pageSize}`,
        { headers },
      ),
    ),
  );

  const remainingTickets = await Promise.all(
    remainingPages.map(async (res) => {
      if (!res.ok) {
        return [] as Ticket[];
      }
      return await res.json() as Ticket[];
    }),
  );

  return firstPage.concat(...remainingTickets);
}

async function refreshTechnicianWorkloads(headers: Record<string, string>): Promise<void> {
  const [assignedTickets, inProgressTickets] = await Promise.all([
    fetchTicketsByStatus(headers, 'assigned'),
    fetchTicketsByStatus(headers, 'in_progress'),
  ]);

  const workloadMap = new Map<string, number>();
  const uniqueTickets = new Map<string, Ticket>();
  [...assignedTickets, ...inProgressTickets].forEach((ticket) => {
    uniqueTickets.set(ticket.id, ticket);
  });

  uniqueTickets.forEach((ticket) => {
    const assignedTechIds = ticket.assignedUserIds || [];
    assignedTechIds.forEach((techId) => {
      workloadMap.set(techId, (workloadMap.get(techId) || 0) + 1);
    });
  });

  technicianWorkloadMapSignal.value = workloadMap;
}

export function unmountTicketsPage(): void {
  // 1. Dispose reactive effect to prevent memory leaks
  if (_ticketsEffectDisposer) {
    _ticketsEffectDisposer();
    _ticketsEffectDisposer = null;
  }

  // 1.5 Clear polling interval
  if (_ticketsPollInterval) {
    clearInterval(_ticketsPollInterval);
    _ticketsPollInterval = null;
  }

  // 1.7 Clear keyboard shortcut listeners
  if (_ticketsKeyDownHandler) {
    window.removeEventListener('keydown', _ticketsKeyDownHandler);
    _ticketsKeyDownHandler = null;
  }

  // 2. Capture scroll position before DOM is torn down
  const listContainer = document.querySelector<HTMLElement>('.page-content-wrapper');
  if (listContainer) {
    _ticketsScrollY = listContainer.scrollTop;
  }

  // 3. Persist navigation state to sessionStorage
  try {
    sessionStorage.setItem('tickets_status_filter', ticketsStatusFilterSignal.peek() ?? 'open');
    sessionStorage.setItem('tickets_page', String(ticketsPageSignal.peek()));
    sessionStorage.setItem('tickets_page_size', String(ticketsPageSizeSignal.peek()));
    sessionStorage.setItem('tickets_selected_id', selectedTicketIdSignal.peek() ?? '');
    sessionStorage.setItem('tickets_scroll_y', String(_ticketsScrollY));
  } catch (e) {
    console.warn('[tickets] Failed to persist page state:', e);
  }
}

export async function refreshTickets() {
  ticketsLoadingSignal.value = true;
  try {
    const h = getAuthHeaders();
    if (allCustomersSignal.peek().length === 0) {
      await initAssetsSignals().catch(err => console.error('Failed to load ticket metadata:', err));
    }
    await refreshConsumables();

    if (canManageTicketAssignments()) {
      await refreshAssignableUsers(h);
    } else if (ticketUsersMapSignal.peek().size > 0) {
      ticketUsersMapSignal.value = new Map();
      technicianWorkloadMapSignal.value = new Map();
    }

    // Fetch tickets with server-side pagination & filter parameters
    const page = ticketsPageSignal.value;
    const pageSize = ticketsPageSizeSignal.value;
    const filter = ticketsStatusFilterSignal.value;
    const searchQuery = ticketsSearchQuerySignal.value.trim();
    let url = `/api/v1/tickets?page=${page}&pageSize=${pageSize}`;

    if (searchQuery) {
      url += `&search=${encodeURIComponent(searchQuery)}`;
    } else if (filter !== 'all') {
      url += `&status=${encodeURIComponent(filter)}`;
    }

    const ticketsRes = await fetch(url, { headers: h });
    if (ticketsRes.ok) {
      const countHeader = ticketsRes.headers.get('X-Total-Count');
      if (countHeader) {
        ticketsTotalCountSignal.value = Number(countHeader) || 0;
      } else {
        // Fallback to active length
        ticketsTotalCountSignal.value = 0;
      }

      const tickets = await ticketsRes.json() as Ticket[];
      ticketsListSignal.value = tickets;

      // Populate ticketAssignmentsSignal map from the API response
      const updated = new Map<string, string[]>();
      tickets.forEach((t: Ticket) => {
        if (t.assignedUserIds) {
          updated.set(t.id, t.assignedUserIds);
        } else {
          updated.set(t.id, []);
        }
      });
      ticketAssignmentsSignal.value = updated;
    } else {
      ticketsListSignal.value = [];
      ticketsTotalCountSignal.value = 0;
    }
    await refreshTicketStatusCounts(h, searchQuery);
  } catch (e) {
    console.error('Failed to fetch tickets', e);
  } finally {
    ticketsLoadingSignal.value = false;
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────────


function priorityBadgeTone(priority: string): string {
  switch (priority) {
    case 'critical': return 'critical';
    case 'high': return 'warning';
    case 'medium': return 'info';
    case 'low': return 'success';
    default: return 'info';
  }
}

function statusBadgeTone(status: string): string {
  switch (status) {
    case 'open': return 'info';
    case 'assigned': return 'attention';
    case 'in_progress': return 'warning';
    case 'resolved': return 'success';
    case 'closed': return 'new';
    default: return 'info';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'open': return 'Open';
    case 'assigned': return 'Awaiting Visit';
    case 'in_progress': return 'Service Active';
    case 'resolved': return 'Resolved';
    case 'closed': return 'Closed';
    default: return status;
  }
}

function priorityLabel(priority: string): string {
  return priority.charAt(0).toUpperCase() + priority.slice(1);
}

function getCustomerName(customerId: string): string {
  const customer = (allCustomersSignal.peek() || []).find((item) => item.id === customerId);
  return customer?.name || 'Unknown';
}

function getAssignedTechIds(ticket: Ticket): string[] {
  const mappedAssignments = ticketAssignmentsSignal.peek().get(ticket.id);
  if (mappedAssignments) {
    return mappedAssignments;
  }

  return ticket.assignedUserIds || [];
}

function getEffectiveTicketStatus(ticket: Ticket): Exclude<TicketStatusFilterKey, 'all'> {
  if (ticket.status === 'closed' || ticket.status === 'resolved' || ticket.status === 'in_progress') {
    return ticket.status;
  }

  return getAssignedTechIds(ticket).length > 0 ? 'assigned' : 'open';
}

function getFilteredTickets(): Ticket[] {
  return ticketsListSignal.peek() || [];
}

function getSelectedTicket(): Ticket | null {
  const id = selectedTicketIdSignal.peek();
  if (!id) return null;
  return (ticketsListSignal.peek() || []).find(t => t.id === id) || null;
}

function parsePartDescription(eventType: string, description: string, consumables: any[]): { partName: string; quantity?: number; status?: string } {
  const desc = description || '';
  let consumableId = '';
  let colloquialName = '';
  let quantity: number | undefined = undefined;
  let status: string | undefined = undefined;

  if (eventType === 'parts_requested') {
    const idMatch = desc.match(/Consumable ID:\s*([a-fA-F0-9-]+)/i);
    const partMatch = desc.match(/Part:\s*([^.]+)/i);
    const qtyMatch = desc.match(/Quantity:\s*(\d+)/i);
    if (idMatch && idMatch[1]) consumableId = idMatch[1];
    if (partMatch && partMatch[1]) colloquialName = partMatch[1].trim();
    if (qtyMatch && qtyMatch[1]) quantity = parseInt(qtyMatch[1], 10);
  } else if (eventType === 'parts_dispatched') {
    const idMatch = desc.match(/Part dispatched:\s*([^.]+)/i);
    if (idMatch && idMatch[1]) {
      const val = idMatch[1].trim();
      if (val.match(/^[a-fA-F0-9-]{36}$/)) consumableId = val;
      else colloquialName = val;
    }
  } else if (eventType === 'parts_installed') {
    const idMatch = desc.match(/Part installed:\s*([^.]+)/i);
    if (idMatch && idMatch[1]) {
      const val = idMatch[1].trim();
      if (val.match(/^[a-fA-F0-9-]{36}$/)) consumableId = val;
      else colloquialName = val;
    }
  } else if (eventType === 'parts_approved') {
    const idMatch = desc.match(/Part approved:\s*([^.]+)/i);
    const statusMatch = desc.match(/Status:\s*(\w+)/i);
    if (idMatch && idMatch[1]) {
      const val = idMatch[1].trim();
      if (val.match(/^[a-fA-F0-9-]{36}$/)) consumableId = val;
      else colloquialName = val;
    }
    if (statusMatch && statusMatch[1]) status = statusMatch[1];
  }

  if (!consumableId && !colloquialName) {
    const uuidMatch = desc.match(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/);
    if (uuidMatch) {
      consumableId = uuidMatch[0];
    } else {
      const fallbackMatch = desc.match(/Part(?:\s*\w+)?:\s*([^.]+)/i);
      if (fallbackMatch && fallbackMatch[1]) {
        colloquialName = fallbackMatch[1].trim();
      }
    }
  }

  const consumable = consumables.find(c => c.id === consumableId);
  const partName = consumable ? consumable.name : (colloquialName || (consumableId ? `Part (${consumableId.substring(0, 8)})` : 'Part Recommended'));

  return { partName, quantity, status };
}

function formatCounterReadingDetails(counters: any, description?: string | null): string {
  const pageTypes = pageTypesSignal.peek() || [];
  const ptMap = new Map(pageTypes.map((pt: any) => [pt.id, pt.displayName || pt.code || pt.name]));

  const resolveTypeLabel = (key: string): string => {
    if (ptMap.has(key)) return ptMap.get(key)!;
    const lk = key.toLowerCase();
    if (lk.includes('bw') || lk.includes('mono') || lk.includes('black')) return 'B&W (A4)';
    if (lk.includes('color') || lk.includes('colour')) return 'Color (A4)';
    if (lk.includes('large_bw') || lk.includes('a3_bw')) return 'B&W (A3)';
    if (lk.includes('large_color') || lk.includes('a3_color')) return 'Color (A3)';
    return key.replace(/_/g, ' ').toUpperCase();
  };

  if (counters && typeof counters === 'object' && Object.keys(counters).length > 0) {
    const formattedEntries = Object.entries(counters).map(([key, val]) => {
      const typeLabel = resolveTypeLabel(key);
      const countVal = Number(val);
      const countStr = isNaN(countVal) ? String(val) : countVal.toLocaleString();
      return `${typeLabel}: ${countStr} pages`;
    });
    return formattedEntries.join(' · ');
  }

  if (description) {
    let cleanDesc = description.replace(/Reading ID:\s*[0-9a-f-]{36}/gi, '').replace(/\b[0-9a-f-]{36}\b/gi, '').trim();
    cleanDesc = cleanDesc.replace(/^[\s:·,-]+|[\s:·,-]+$/g, '');
    if (cleanDesc) {
      return cleanDesc;
    }
  }

  return 'Meter counter reading recorded';
}

function renderLifecycleEventCard(evt: components['schemas']['TicketLifecycleEvent'], consumables: components['schemas']['Consumable'][]): string {
  const eventType: string = evt.eventType || 'unknown';
  const rawDesc = evt.description || '';
  const dateStr: string = formatDateTime(evt.createdAt);

  let dotColor = 'var(--p-color-border-subdued, #c4cdd5)';
  let badgeTone = 'new';
  let label = eventType;
  let subSectionHtml = '';

  const isPartsEvent = eventType === 'parts_requested' ||
    eventType === 'parts_dispatched' ||
    eventType === 'parts_installed' ||
    eventType === 'parts_approved';

  if (isPartsEvent) {
    const { partName: rawPartName, quantity, status } = parsePartDescription(eventType, rawDesc, consumables);
    const partName = escapeHtml(rawPartName);

    let subSectionTitle = 'Parts Recommended:';
    let statusText = eventType === 'parts_requested' ? 'requested' :
      eventType === 'parts_dispatched' ? 'dispatched' :
        eventType === 'parts_installed' ? 'installed' :
          status || 'approved';

    if (eventType === 'parts_dispatched') {
      dotColor = 'var(--p-color-text-info, #005bd3)';
      badgeTone = 'info';
      label = 'Part Dispatched';
    } else if (eventType === 'parts_installed') {
      dotColor = 'var(--p-color-text-success, #008060)';
      badgeTone = 'success';
      label = 'Part Installed';
    } else if (eventType === 'parts_approved') {
      dotColor = 'var(--p-color-text-success, #008060)';
      badgeTone = statusText.includes('rejected') ? 'critical' : 'success';
      label = statusText.includes('rejected') ? 'Part Rejected' : 'Part Approved';
    } else {
      dotColor = 'var(--p-color-text-attention, #b45309)';
      badgeTone = 'attention';
      label = 'Parts Requested';
    }

    subSectionHtml = `
      <div style="margin-top:0.5rem;background:var(--p-color-bg-surface-secondary, #f9fafb);border-radius:4px;padding:0.5rem;">
        <s-text style="font-weight:600;font-size:0.75rem;display:block;margin-bottom:0.25rem;">${subSectionTitle}</s-text>
        <div style="display:flex;justify-content:space-between;align-items:center;font-size:0.75rem;">
          <span>${quantity ? `Qty ${quantity} × ` : ''}<s-text tone="subdued">${partName}</s-text></span>
          <s-badge tone="${badgeTone}">${escapeHtml(statusText)}</s-badge>
        </div>
      </div>
    `;
  } else if (eventType === 'counter_reported' || eventType === 'reading_submitted' || eventType === 'reading_created' || eventType === 'counter_updated' || eventType === 'meter_reading') {
    dotColor = 'var(--p-color-text-info, #005bd3)';
    badgeTone = 'info';
    label = 'Counter Reported';
    const counterText = escapeHtml(formatCounterReadingDetails((evt as any).counters, rawDesc));
    subSectionHtml = `
      <div style="margin-top:0.5rem;background:var(--p-color-bg-surface-secondary, #f9fafb);border-radius:4px;padding:0.5rem;border-left:3px solid var(--p-color-text-info, #005bd3);">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.25rem;">
          <s-text style="font-weight:600;font-size:0.75rem;">Counter Reported:</s-text>
          <s-badge tone="info">Meter Reading</s-badge>
        </div>
        <s-text tone="subdued" style="font-size:0.75rem;">${counterText}</s-text>
      </div>
    `;
  } else if (eventType === 'session_started') {
    dotColor = 'var(--p-color-text-info, #005bd3)';
    badgeTone = 'info';
    label = 'Service Visit Started';
  } else if (eventType === 'session_stopped' || eventType === 'session_finished') {
    dotColor = 'var(--p-color-text-success, #008060)';
    badgeTone = 'success';
    label = 'Service Visit Ended';
  } else if (eventType === 'created') {
    dotColor = 'var(--p-color-text-info, #005bd3)';
    badgeTone = 'new';
    label = 'Ticket Created';
  } else if (eventType === 'assigned') {
    dotColor = 'var(--p-color-text-attention, #b45309)';
    badgeTone = 'attention';
    label = 'Technician Assigned';
  } else if (eventType === 'closed') {
    dotColor = 'var(--p-color-border-subdued, #c4cdd5)';
    badgeTone = 'subdued';
    label = 'Ticket Closed';
  } else if (eventType === 'reopened') {
    dotColor = 'var(--p-color-text-critical, #d72c0d)';
    badgeTone = 'critical';
    label = 'Ticket Reopened';
  }

  const badgeText = eventType.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return `
    <div style="display:flex;align-items:flex-start;gap:0.75rem;padding:0.5rem 0;border-bottom:1px solid var(--p-color-border-subdued, #f1f2f4);">
      <div style="width:8px;height:8px;border-radius:50%;background:${dotColor};margin-top:6px;flex-shrink:0;"></div>
      <s-stack gap="extraSmall" style="flex:1;">
        <s-stack direction="inline" gap="small" align="center" style="width:100%;justify-content:space-between;">
          <s-text style="font-weight:600;font-size:0.85rem;">${label}</s-text>
          <s-badge tone="${badgeTone}">${badgeText}</s-badge>
        </s-stack>
        <s-text tone="subdued" style="font-size:0.8rem;">${dateStr}</s-text>
        ${subSectionHtml}
      </s-stack>
    </div>
  `;
}

function renderSessionCard(sess: ServiceSessionWithParts, usersMap: Map<string, User>, consumables: components['schemas']['Consumable'][]): string {
  const currentUser = userProfileSignal.peek();
  const techUser = usersMap.get(sess.technicianId) || (currentUser?.id === sess.technicianId ? currentUser : null);
  const rawTechEmail = techUser?.email || (sess.technicianId ? `Tech (${sess.technicianId.substring(0, 8)})` : 'Technician');
  const techEmail = escapeHtml(rawTechEmail);

  const startTimeStr = formatDateTime(sess.startedAt);
  const endTimeStr = sess.endedAt ? formatDateTime(sess.endedAt) : null;
  const duration = formatDurationWords(sess.startedAt, sess.endedAt || null);
  const summaryText = escapeHtml(sess.summary || 'No summary notes provided.');

  // Photos
  const photosHtml = sess.photoUrls && sess.photoUrls.length > 0
    ? `
      <div style="display:flex;gap:0.5rem;margin-top:0.5rem;flex-wrap:wrap;">
        ${sess.photoUrls.map((url: string) => {
      const cleanUrl = escapeHtml(url);
      return `
          <a href="${cleanUrl}" target="_blank" style="display:block;border:1px solid #c4cdd5;border-radius:4px;overflow:hidden;width:60px;height:60px;">
            <img src="${cleanUrl}" style="width:100%;height:100%;object-fit:cover;" />
          </a>
        `;
    }).join('')}
      </div>
    `
    : '';

  // Parts
  const partsHtml = sess.parts && sess.parts.length > 0
    ? `
      <div style="margin-top:0.5rem;background:var(--p-color-bg-surface-secondary, #f9fafb);border-radius:4px;padding:0.5rem;">
        <s-text style="font-weight:600;font-size:0.75rem;display:block;margin-bottom:0.25rem;">Parts Recommended:</s-text>
        <s-stack gap="extraSmall">
          ${sess.parts.map((p: components['schemas']['PartRecommendation']) => {
      const consumable = consumables.find(c => c.id === p.consumableId);
      const rawPartName = p.colloquialName || (consumable ? consumable.name : p.consumableId) || 'Unknown Part';
      const partName = escapeHtml(rawPartName);
      const cleanStatus = escapeHtml(p.status);
      return `
              <div style="display:flex;justify-content:space-between;align-items:center;font-size:0.75rem;">
                <span>Qty ${p.quantity} × <s-text tone="subdued">${partName}</s-text></span>
                <s-badge tone="${p.status.includes('approved') ? 'success' : p.status.includes('rejected') ? 'critical' : 'attention'}">${cleanStatus}</s-badge>
              </div>
            `;
    }).join('')}
        </s-stack>
      </div>
    `
    : '';

  const readingInfo = (sess as any).readingId || (sess as any).counters || (sess as any).reading;
  const counterHtml = readingInfo
    ? `
      <div style="margin-top:0.5rem;background:var(--p-color-bg-surface-secondary, #f9fafb);border-radius:4px;padding:0.5rem;border-left:3px solid var(--p-color-text-info, #005bd3);">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.25rem;">
          <s-text style="font-weight:600;font-size:0.75rem;">Counter Reported:</s-text>
          <s-badge tone="info">Meter Reading</s-badge>
        </div>
        <s-text tone="subdued" style="font-size:0.75rem;">
          ${escapeHtml(formatCounterReadingDetails((sess as any).counters, (sess as any).readingSummary || null))}
        </s-text>
      </div>
    `
    : '';

  const badgeTone = sess.isFinal ? 'success' : sess.endedAt ? 'info' : 'attention';
  const badgeLabel = sess.isFinal ? 'Final Fix' : sess.endedAt ? 'Visit Completed' : 'Service Active';

  return `
    <div style="display:flex;align-items:flex-start;gap:0.75rem;padding:0.75rem 0;border-bottom:1px solid var(--p-color-border-subdued, #f1f2f4);">
      <div style="width:8px;height:8px;border-radius:50%;background:var(--p-color-border-accent, #008060);margin-top:6px;flex-shrink:0;"></div>
      <s-stack gap="extraSmall" style="flex:1;">
        <s-stack direction="inline" gap="small" align="center" style="width:100%;justify-content:space-between;">
          <s-text style="font-weight:600;font-size:0.875rem;">${sess.isFinal ? 'Final Fix Visit' : 'Service Visit'}</s-text>
          <s-badge tone="${badgeTone}">${badgeLabel}</s-badge>
        </s-stack>
        <div style="font-size:0.8rem;color:var(--p-color-text-subdued, #6d7175);display:flex;flex-wrap:wrap;gap:0.35rem 0.85rem;margin-top:0.15rem;">
          <span><strong>Technician:</strong> ${techEmail}</span>
          <span><strong>Started:</strong> ${startTimeStr}</span>
          ${endTimeStr ? `<span><strong>Ended:</strong> ${endTimeStr}</span>` : ''}
          <span><strong>Duration:</strong> ${duration}</span>
        </div>
        <s-box padding="small" background="surface-secondary" borderRadius="base" style="margin-top:0.35rem;">
          <s-text style="font-size:0.825rem;white-space:pre-wrap;">${summaryText}</s-text>
          ${photosHtml}
          ${partsHtml}
          ${counterHtml}
        </s-box>
      </s-stack>
    </div>
  `;
}

/** Updates filter tab counts and active states in-place without re-rendering. */
function updateFilterTabsInPlace(): void {
  const activeFilter = ticketsStatusFilterSignal.value;
  const counts = ticketsStatusCountsSignal.value;

  document.querySelectorAll('.ticket-filter-tab').forEach(tab => {
    const key = (tab as HTMLElement).dataset.filter || '';
    const isActive = key === activeFilter;
    if (isActive) {
      tab.classList.add('ticket-filter-tab--active');
    } else {
      tab.classList.remove('ticket-filter-tab--active');
    }
    const countSpan = tab.querySelector('.ticket-filter-count');
    if (countSpan) {
      countSpan.textContent = String(counts[key as keyof typeof counts] || 0);
    }
  });
}

function renderEmptyDetailPanel(): string {
  return `
    <div style="padding: 2.5rem; text-align: center;">
      <s-stack gap="base" align="center">
        <s-text tone="subdued" style="font-size: 1.1rem; font-weight: 500;">Select a ticket to view details</s-text>
        <s-text tone="subdued" style="font-size: 0.875rem;">Click on any ticket in the table to inspect history and update status.</s-text>
      </s-stack>
    </div>
  `;
}

// ─── Detail Panel Renderer ─────────────────────────────────────────────────────

function renderDetailPanel(): string {
  const ticket = getSelectedTicket();
  if (!ticket) {
    return renderEmptyDetailPanel();
  }

  const customers = allCustomersSignal.peek() || [];
  const sites = sitesSignal.peek() || [];
  const assets = assetsListSignal.peek() || [];
  const usersMap = ticketUsersMapSignal.peek();

  const rawCustName = customers.find(c => c.id === ticket.customerId)?.name || 'Unknown Customer';
  const custName = escapeHtml(rawCustName);
  const rawSiteName = ticket.siteId
    ? (sites.find(s => s.id === ticket.siteId)?.name || 'Unknown Site')
    : '—';
  const siteName = escapeHtml(rawSiteName);

  // Asset resolution: use linked asset or raw model/serial fields
  let assetDisplay = '—';
  if (ticket.assetId) {
    const asset = assets.find(a => a.id === ticket.assetId);
    if (asset) {
      const model = (deviceModelsSignal.peek() || []).find(m => m.id === asset.modelId);
      const modelName = model ? `${model.vendor} ${model.modelName}` : 'Unknown Model';
      assetDisplay = `<s-link href="#machine-history?id=${encodeURIComponent(asset.id)}">${escapeHtml(`${modelName} (SN: ${asset.serialNumber || 'N/A'})`)}</s-link>`;
    } else {
      assetDisplay = escapeHtml(`Asset ${ticket.assetId.substring(0, 8)}…`);
    }
  } else if (ticket.modelNameRaw || ticket.serialNumberRaw) {
    assetDisplay = escapeHtml(`${ticket.modelNameRaw || 'Unknown'} (SN: ${ticket.serialNumberRaw || 'N/A'})`);
  }

  // Assigned technicians (from ticket_assignments if available)
  const assignedTechIds = getAssignedTechIds(ticket);
  const assignedTechsHtml = assignedTechIds.length > 0
    ? assignedTechIds.map(id => {
      const user = usersMap.get(id);
      const name = user ? user.email.split('@')[0] : 'Unknown';
      return `<s-badge tone="info">${escapeHtml(name)}</s-badge>`;
    }).join(' ')
    : '<s-text tone="subdued">Unassigned</s-text>';

  const userRole = userProfileSignal.peek()?.role ?? activeRoleSignal.peek();
  const isAdminOrManager = userRole === 'superadmin' || userRole === 'admin' || userRole === 'manager';
  const isTechnician = userRole === 'technician';
  const showConsoleBtn = (isTechnician || isAdminOrManager);

  const sessions = ticketSessionsSignal.peek() || [];
  const timelineEvents = ticketTimelineEventsSignal.peek() || [];
  const consumables = consumablesSignal.peek() || [];

  // Build a single sorted timeline array (newest first)
  type TimelineEntry = { type: 'event' | 'session'; timestamp: string; data: any };
  const mergedTimeline: TimelineEntry[] = [];

  // Add lifecycle events (filtering out session start/stop/finish events and child counter events tied to sessions)
  timelineEvents.forEach((item: any) => {
    if (item.type === 'event' && item.event) {
      const evt = item.event;
      if (evt.eventType === 'session_started' || evt.eventType === 'session_stopped' || evt.eventType === 'session_finished' || evt.eventType === 'session_canceled') {
        return;
      }
      if ((evt.eventType === 'counter_reported' || evt.eventType === 'reading_submitted' || evt.eventType === 'reading_created' || evt.eventType === 'counter_updated') && (evt.sessionId || item.sessionId)) {
        return;
      }
      mergedTimeline.push({ type: 'event', timestamp: evt.createdAt || item.timestamp, data: evt });
    } else if (item.type === 'session' && item.session) {
      const localSession = sessions.find(s => s.id === item.session.id);
      const sessData = localSession || item.session;
      if (!mergedTimeline.some(e => e.type === 'session' && e.data.id === sessData.id)) {
        mergedTimeline.push({ type: 'session', timestamp: sessData.startedAt || item.timestamp, data: sessData });
      }
    }
  });

  // Ensure all sessions are included
  sessions.forEach((sess: any) => {
    if (!mergedTimeline.some(e => e.type === 'session' && e.data.id === sess.id)) {
      mergedTimeline.push({ type: 'session', timestamp: sess.startedAt, data: sess });
    }
  });

  // Ensure synthetic created event is included if not in timelineEvents
  if (!mergedTimeline.some(e => e.type === 'event' && e.data.eventType === 'created')) {
    mergedTimeline.push({
      type: 'event',
      timestamp: ticket.createdAt,
      data: { eventType: 'created', createdAt: ticket.createdAt, actorEmail: null, description: null },
    });
  }

  // Sort descending (newest first)
  mergedTimeline.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  // Render each timeline entry
  const timelineHtml = mergedTimeline.map(entry => {
    if (entry.type === 'event') {
      return renderLifecycleEventCard(entry.data, consumables);
    } else {
      return renderSessionCard(entry.data, usersMap, consumables);
    }
  }).join('');

  // Reopen button for closed tickets (manager+ only)
  const canReopen = isAdminOrManager && ticket.status === 'closed';
  const effectiveStatus = getEffectiveTicketStatus(ticket);

  const cleanTicketNumber = escapeHtml(ticket.ticketNumber);
  const cleanDescription = escapeHtml(ticket.description || 'No description provided.');
  const rawContactNumber = ticket.contactNumber ? ticket.contactNumber.trim() : '';
  const cleanContactNumber = escapeHtml(rawContactNumber || '—');
  const cleanContactEmail = ticket.contactEmail ? escapeHtml(ticket.contactEmail) : '';
  const phoneLink = rawContactNumber
    ? `<a href="tel:${escapeHtml(rawContactNumber)}" style="color:var(--p-color-text-interactive, #005bd3);text-decoration:underline;font-weight:600;">${cleanContactNumber}</a>`
    : '—';
  const contactDisplay = phoneLink + (cleanContactEmail ? ` · ${cleanContactEmail}` : '');

  return `
    <div class="ticket-detail-panel">
      <!-- Detail Header -->
      <div style="padding:1.25rem 1.25rem 0.75rem;border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);">
        <s-stack gap="small">
          <s-stack direction="inline" gap="small" align="center" style="justify-content:space-between;width:100%;" wrap>
            <s-stack direction="inline" gap="small" align="center" wrap>
              <s-heading element="h2" style="font-size:1.1rem;font-weight:700;margin:0;">${cleanTicketNumber}</s-heading>
              <s-badge tone="${statusBadgeTone(effectiveStatus)}">${statusLabel(effectiveStatus)}</s-badge>
              <s-badge tone="${priorityBadgeTone(ticket.priority)}">${priorityLabel(ticket.priority)}</s-badge>
            </s-stack>
            <button id="btn-toggle-ticket-detail-fullscreen" title="Toggle Fullscreen" style="background:none;border:1px solid var(--p-color-border-subdued,#c9cccf);cursor:pointer;padding:4px 10px;border-radius:4px;display:inline-flex;align-items:center;gap:6px;font-size:0.75rem;font-weight:600;color:var(--p-color-text,#202223);margin-left:auto;">
              <svg id="svg-ticket-fullscreen-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              </svg>
              <span id="txt-ticket-fullscreen-label">Expand</span>
            </button>
          </s-stack>
          <s-text tone="subdued" style="font-size:0.8rem;">Created ${formatDateTime(ticket.createdAt)}</s-text>
          <div style="display:flex;gap:0.5rem;margin-top:0.5rem;flex-wrap:wrap;">
            ${showConsoleBtn && ticket.status !== 'closed' ? `
              <s-button variant="primary" size="slim" id="btn-open-tech-console">
                Open Technician Console
              </s-button>
            ` : ''}
            ${isAdminOrManager && ticket.status !== 'closed' ? `
              <s-button variant="secondary" size="slim" id="btn-edit-ticket-manager">
                Edit Ticket
              </s-button>
              <s-button variant="monochromePlain" size="slim" id="btn-close-ticket-manager">
                ❌ Close Ticket
              </s-button>
            ` : ''}
            ${canReopen ? `
              <s-button variant="primary" size="slim" id="btn-reopen-ticket">
                <s-icon type="redo" size="small"></s-icon>
                Reopen Ticket
              </s-button>
            ` : ''}
          </div>
        </s-stack>
      </div>

      <!-- Detail Body -->
      <div id="ticket-detail-scroll-container" style="padding:1rem 1.25rem;overflow-y:auto;flex:1;">
        <s-stack gap="base">
          <!-- Meta Grid -->
          <s-box padding="base" background="surface-secondary" borderRadius="base">
            <s-stack gap="small">
              <div style="display:grid;grid-template-columns:110px 1fr;gap:0.35rem 0.75rem;font-size:0.875rem;">
                <s-text tone="subdued" style="font-weight:600;">Customer</s-text>
                <s-text>${custName}</s-text>
                <s-text tone="subdued" style="font-weight:600;">Site</s-text>
                <s-text>${siteName}</s-text>
                <s-text tone="subdued" style="font-weight:600;">Asset</s-text>
                <s-text>${assetDisplay}</s-text>
                <s-text tone="subdued" style="font-weight:600;">Contact</s-text>
                <s-text>${contactDisplay}</s-text>
              </div>
            </s-stack>
          </s-box>

          <!-- Description -->
          <s-stack gap="small">
            <s-text style="font-weight:600;font-size:0.8rem;text-transform:uppercase;color:var(--p-color-text-subdued);">Description</s-text>
            <s-box padding="base" background="surface" border="base" borderRadius="base">
              <s-paragraph>${cleanDescription}</s-paragraph>
            </s-box>
          </s-stack>

          <!-- Assigned Technicians -->
          <s-stack gap="small">
            <s-stack direction="inline" gap="small" align="center">
              <s-text style="font-weight:600;font-size:0.8rem;text-transform:uppercase;color:var(--p-color-text-subdued);">Assigned Technicians</s-text>
              ${isAdminOrManager ? `
              <s-button
                id="btn-open-assign-modal"
                size="slim"
                variant="plain"
                commandFor="assign-techs-modal"
                command="--show"
              >
                <s-icon type="edit" size="small"></s-icon>
                Assign
              </s-button>
              ` : ''}
            </s-stack>
            <s-box padding="small" background="surface" border="base" borderRadius="base">
              ${assignedTechsHtml}
            </s-box>
          </s-stack>

          <!-- Unified Chronological Timeline -->
          <s-stack gap="small">
            <s-text style="font-weight:600;font-size:0.8rem;text-transform:uppercase;color:var(--p-color-text-subdued);">Timeline</s-text>
            <s-box padding="base" background="surface" border="base" borderRadius="base">
              <s-stack gap="small">
                ${ticketSessionsLoadingSignal.peek() ? `
                  <div style="padding:2rem;text-align:center;display:flex;flex-direction:column;align-items:center;gap:0.5rem;">
                    <s-spinner size="small"></s-spinner>
                    <s-text tone="subdued" style="font-size:0.85rem;">Loading timeline...</s-text>
                  </div>
                ` : (timelineHtml || `
                  <div style="padding:1rem;text-align:center;">
                    <s-text tone="subdued">No timeline events yet.</s-text>
                  </div>
                `)}
                <div style="margin-bottom:2rem;"></div>
              </s-stack>
            </s-box>
          </s-stack>
        </s-stack>
      </div>
    </div>

    <!-- Reopen Reason Modal -->
    <s-modal id="reopen-ticket-modal" heading="Reopen Ticket">
      <s-stack gap="base">
        <s-text tone="subdued">Provide a reason for reopening this ticket.</s-text>
        <s-text-area
          id="reopen-reason-input"
          label="Reason"
          name="reopenReason"
          placeholder="e.g. Issue recurred after repair…"
          rows="3"
          required
        ></s-text-area>
        <div id="reopen-error" style="display:none;color:var(--p-color-text-critical);font-size:0.875rem;"></div>
        <s-stack direction="inline" gap="small" style="margin-top:0.5rem;">
          <s-button variant="primary" id="btn-confirm-reopen">Reopen Ticket</s-button>
          <s-button commandFor="reopen-ticket-modal" command="--hide">Cancel</s-button>
        </s-stack>
      </s-stack>
    </s-modal>
  `;
}

// ─── Assign Technicians Modal ──────────────────────────────────────────────────

function renderAssignModal(): string {
  return `
    <s-modal id="assign-techs-modal" heading="Assign Technicians">
      <s-stack gap="base">
        <s-text tone="subdued">Select technicians to assign to this ticket.</s-text>

        <s-text-field
          id="assign-techs-search"
          placeholder="Filter technicians by email…"
          label="Search Technicians"
        ></s-text-field>

        <s-box padding="base" background="surface-secondary" borderRadius="base" style="max-height: 250px; overflow-y: auto;">
          <s-stack gap="small" id="assign-techs-list-container">
            ${renderAssignTechniciansListHtml()}
          </s-stack>
        </s-box>
        <div id="assign-techs-error" style="display:none;color:var(--p-color-text-critical);font-size:0.875rem;"></div>
        <s-stack direction="inline" gap="small" style="margin-top:0.5rem;">
          <s-button variant="primary" id="btn-submit-assign">Save Assignments</s-button>
          <s-button commandFor="assign-techs-modal" command="--hide">Cancel</s-button>
        </s-stack>
      </s-stack>
    </s-modal>
  `;
}

function renderAssignTechniciansListHtml(): string {
  const usersMap = ticketUsersMapSignal.peek();
  const ticket = getSelectedTicket();
  const currentAssignments = ticket ? (ticketAssignmentsSignal.peek().get(ticket.id) || []) : [];
  const loadError = assignableUsersLoadErrorSignal.peek();

  // Filter technicians only
  const technicians: any[] = [];
  usersMap.forEach((user) => {
    if (user.role === 'technician') {
      technicians.push(user);
    }
  });

  const workloadMap = technicianWorkloadMapSignal.peek();

  const siteTechs: any[] = [];
  const otherTechs: any[] = [];

  technicians.forEach(t => {
    const isSiteMatch = !!(ticket && ticket.siteId && t.sites && t.sites.includes(ticket.siteId));
    const workload = workloadMap.get(t.id) || 0;
    const info = { id: t.id, email: t.email, workload, isSiteMatch };
    if (isSiteMatch) {
      siteTechs.push(info);
    } else {
      otherTechs.push(info);
    }
  });

  const renderTechRow = (t: any) => {
    const checked = currentAssignments.includes(t.id) ? 'checked' : '';
    const label = `${t.email} (workload: ${t.workload} active)`;
    const cleanId = escapeHtml(t.id);
    const cleanEmail = escapeHtml(t.email.toLowerCase());
    const cleanLabel = escapeHtml(label);
    return `
      <div class="assign-tech-row" data-email="${cleanEmail}" style="margin-bottom:0.25rem;">
        <s-checkbox
          label="${cleanLabel}"
          name="assign-tech-${cleanId}"
          value="${cleanId}"
          ${checked}
        ></s-checkbox>
      </div>
    `;
  };

  const siteTechsHtml = siteTechs.length > 0
    ? `
      <div style="margin-bottom:0.75rem;">
        <s-text style="font-weight:600;font-size:0.8rem;text-transform:uppercase;color:var(--p-color-text-success, #008060);margin-bottom:0.25rem;display:block;">Primary Site Technicians</s-text>
        ${siteTechs.map(renderTechRow).join('')}
      </div>
    `
    : '';

  const otherTechsHtml = otherTechs.length > 0
    ? `
      <div>
        <s-text style="font-weight:600;font-size:0.8rem;text-transform:uppercase;color:var(--p-color-text-subdued);margin-bottom:0.25rem;display:block;">Other Available Technicians</s-text>
        ${otherTechs.map(renderTechRow).join('')}
      </div>
    `
    : '';

  const listHtml = loadError
    ? `<s-banner tone="critical">${escapeHtml(loadError)}</s-banner>`
    : (siteTechs.length > 0 || otherTechs.length > 0)
      ? `${siteTechsHtml}${otherTechsHtml}`
      : `<s-text tone="subdued">No technicians found in the system.</s-text>`;

  return listHtml;
}


// ─── Batch Assign Modal ────────────────────────────────────────────────────────

function renderBatchAssignModal(): string {
  const usersMap = ticketUsersMapSignal.peek();
  const loadError = assignableUsersLoadErrorSignal.peek();
  const technicians: any[] = [];
  usersMap.forEach((user, id) => {
    if (user.role === 'technician') {
      technicians.push({ id, email: user.email });
    }
  });

  const techCheckboxesHtml = loadError
    ? `<s-banner tone="critical">${escapeHtml(loadError)}</s-banner>`
    : technicians.length > 0
      ? technicians.map(t => {
        const cleanId = escapeHtml(t.id);
        const cleanEmail = escapeHtml(t.email);
        return `
          <s-checkbox
            label="${cleanEmail}"
            name="batch-assign-tech-${cleanId}"
            value="${cleanId}"
          ></s-checkbox>
        `;
      }).join('')
      : `<s-text tone="subdued">No technicians found in the system.</s-text>`;

  return `
    <s-modal id="batch-assign-techs-modal" heading="Batch Assign Technicians">
      <s-stack gap="base">
        <s-text tone="subdued">Select technicians to assign to all selected tickets.</s-text>
        <s-box padding="base" background="surface-secondary" borderRadius="base">
          <s-stack gap="small">
            ${techCheckboxesHtml}
          </s-stack>
        </s-box>
        <div id="batch-assign-techs-error" style="display:none;color:var(--p-color-text-critical);font-size:0.875rem;"></div>
        <s-stack direction="inline" gap="small" style="margin-top:0.5rem;">
          <s-button variant="primary" id="btn-submit-batch-assign">Save Batch Assignments</s-button>
          <s-button commandFor="batch-assign-techs-modal" command="--hide">Cancel</s-button>
        </s-stack>
      </s-stack>
    </s-modal>
  `;
}

function bindAssignSearchListener(): void {
  const searchInput = document.getElementById('assign-techs-search') as HTMLInputElement | null;
  if (!searchInput) {
    return;
  }

  searchInput.addEventListener('input', () => {
    const q = searchInput.value.toLowerCase().trim();
    document.querySelectorAll('.assign-tech-row').forEach(row => {
      const email = row.getAttribute('data-email') || '';
      (row as HTMLElement).style.display = email.includes(q) ? 'block' : 'none';
    });
  });
}

function bindAssignSubmitListener(): void {
  const submitAssignBtn = document.getElementById('btn-submit-assign');
  if (!submitAssignBtn) {
    return;
  }

  submitAssignBtn.addEventListener('click', async () => {
    const ticket = getSelectedTicket();
    if (!ticket) return;

    const checkboxes = document.querySelectorAll<HTMLInputElement>('[name^="assign-tech-"]');
    const selectedIds: string[] = [];
    checkboxes.forEach(cb => {
      if (cb.checked) {
        selectedIds.push(cb.value);
      }
    });

    const errorEl = document.getElementById('assign-techs-error');

    try {
      const res = await fetch(`/api/v1/tickets/${ticket.id}/assign`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ userIds: selectedIds }),
      });

      if (res.ok) {
        const updated = new Map(ticketAssignmentsSignal.value);
        updated.set(ticket.id, selectedIds);
        ticketAssignmentsSignal.value = updated;

        const modal = document.getElementById('assign-techs-modal') as any;
        if (modal && typeof modal.hideOverlay === 'function') modal.hideOverlay();

        showToast('Technician assignment saved successfully.', 'success');

        reRenderDetailPanel();
        await refreshTickets();
      } else {
        const errBody = await res.json().catch(() => ({ error: 'Assignment failed' }));
        if (errorEl) {
          errorEl.style.display = 'block';
          errorEl.textContent = errBody.error || 'Failed to assign technicians.';
        }
      }
    } catch (e) {
      console.error('Assign failed:', e);
      if (errorEl) {
        errorEl.style.display = 'block';
        errorEl.textContent = 'Network error during assignment.';
      }
    }
  });
}

function renderAssignModalShell(): void {
  const assignModalContainer = document.getElementById('assign-modal-outer-container');
  if (!assignModalContainer) {
    return;
  }

  assignModalContainer.innerHTML = renderAssignModal();
  bindAssignSearchListener();
  bindAssignSubmitListener();
}

function refreshAssignTechniciansList(): void {
  const listContainer = document.getElementById('assign-techs-list-container');
  if (listContainer) {
    listContainer.innerHTML = renderAssignTechniciansListHtml();
  }
}

function showExistingAssignModal(): void {
  const modal = document.getElementById('assign-techs-modal') as any;
  if (modal && typeof modal.showOverlay === 'function') {
    modal.showOverlay();
  }
}

async function hydrateAssignTechniciansModal(): Promise<void> {
  if (!canManageTicketAssignments()) {
    return;
  }

  if (!document.getElementById('assign-techs-modal')) {
    renderAssignModalShell();
  }

  showExistingAssignModal();
  refreshAssignTechniciansList();

  const headers = getAuthHeaders();
  try {
    await Promise.all([
      refreshAssignableUsers(headers),
      safeRefreshTechnicianWorkloads(headers),
    ]);
  } catch (err) {
    console.error('Failed to prepare assignment modal', err);
    if (ticketUsersMapSignal.peek().size === 0) {
      assignableUsersLoadErrorSignal.value = 'Unable to load technician accounts.';
    }
  }

  refreshAssignTechniciansList();
}

// ─── Ticket Intake Modal ───────────────────────────────────────────────────────

function renderIntakeModal(): string {
  return `
    <s-modal id="intake-ticket-modal" heading="Create New Ticket">
      <s-stack gap="base">

        <!-- Explicit New Customer Toggle (C3) -->
        <div style="margin-bottom: 0.25rem;">
          <s-checkbox
            id="intake-new-customer-toggle"
            label="This is a new customer profile"
            name="newCustomerToggle"
          ></s-checkbox>
        </div>

        <!-- Customer Autocomplete (only shown if existing customer) -->
        <div id="intake-customer-autocomplete-container" style="position:relative;z-index:100;">
          <opm-autocomplete
            id="intake-customer-autocomplete"
            label="Customer Name"
            placeholder="Search for a customer…"
          ></opm-autocomplete>
        </div>

        <!-- New Customer Name input (shown only if new customer checkbox checked) -->
        <div id="intake-new-customer-name-container" style="display: none;">
          <s-text-field
            id="intake-new-customer-name"
            label="Customer Name"
            name="newCustomerName"
            placeholder="e.g. Acme Corporation"
            required
          ></s-text-field>
        </div>

        <!-- New Customer Details Form Section -->
        <div id="intake-new-customer-section" style="display: none;">
          <s-stack gap="base">
            <s-text-field
              id="intake-contact-number"
              label="Contact Number"
              name="contactNumber"
              placeholder="e.g. 9801234567"
              required
            ></s-text-field>
            <s-email-field
              id="intake-contact-email"
              label="Contact Email"
              name="contactEmail"
              placeholder="e.g. support@company.com"
            ></s-email-field>
            <s-text-field
              id="intake-site-name"
              label="Primary Site Name"
              name="siteName"
              placeholder="e.g. Kathmandu Office"
              required
            ></s-text-field>
          </s-stack>
        </div>

        <!-- Existing Customer Details Form Section -->
        <div id="intake-existing-customer-section" style="display: none;">
          <s-stack gap="base">
            <div style="margin-bottom: 0.25rem;">
              <s-checkbox
                id="intake-new-site-toggle"
                label="This is a new site"
                name="newSiteToggle"
              ></s-checkbox>
            </div>
            <div id="intake-site-autocomplete-container" style="position:relative;z-index:90;">
              <opm-autocomplete
                id="intake-site-autocomplete"
                label="Site"
                placeholder="Search for a site…"
              ></opm-autocomplete>
            </div>
            <div id="intake-new-site-name-container" style="display: none;">
              <s-text-field
                id="intake-new-site-name"
                label="Site Name"
                name="newSiteName"
                placeholder="e.g. Lalitpur Branch"
                required
              ></s-text-field>
            </div>
            <s-text-field
              id="intake-existing-contact-number"
              label="Contact Number"
              name="contactNumber"
              placeholder="e.g. 9801234567"
              required
            ></s-text-field>
            <s-email-field
              id="intake-existing-contact-email"
              label="Contact Email"
              name="contactEmail"
              placeholder="e.g. support@company.com"
            ></s-email-field>
          </s-stack>
        </div>

        <!-- Device Info (always shown, optional) -->
        <s-stack gap="base">
          <s-text style="font-weight:600;font-size:0.8rem;text-transform:uppercase;color:var(--p-color-text-subdued);">Device Information (Optional)</s-text>
          
          <div id="intake-new-asset-toggle-container" style="margin-bottom: 0.25rem;">
            <s-checkbox
              id="intake-new-asset-toggle"
              label="This is a new printer asset"
              name="newAssetToggle"
            ></s-checkbox>
          </div>

          <!-- Existing Asset Autocomplete (shown only if new asset toggle is unchecked) -->
          <div id="intake-existing-asset-autocomplete-container" style="position:relative;z-index:85;">
            <opm-autocomplete
              id="intake-existing-asset-autocomplete"
              label="Search Printer Serial Number"
              placeholder="Type serial number to search…"
            ></opm-autocomplete>
            <div id="intake-no-assets-warning" style="display:none; margin-top:0.5rem;">
              <s-banner tone="critical">
                No active assets found for this customer or site/location. Please check "This is a new printer asset" above to register a new one.
              </s-banner>
            </div>
          </div>

          <!-- New Asset Info (shown only if new asset checkbox checked) -->
          <div id="intake-new-asset-serial-container" style="display: none;">
            <s-text-field
              id="intake-serial-number"
              label="Serial Number"
              name="serialNumber"
              placeholder="e.g. CN-3300-9988"
            ></s-text-field>
          </div>

          <div id="intake-new-asset-model-container" style="position:relative;z-index:80;display: none;">
            <opm-autocomplete
              id="intake-model-autocomplete"
              label="Device Model"
              placeholder="Search for a device model (e.g. HP LaserJet)…"
            ></opm-autocomplete>
          </div>
        </s-stack>

        <!-- Description -->
        <s-text-area
          id="intake-description"
          label="Failure Description"
          name="description"
          placeholder="Describe the issue…"
          rows="3"
          required
        ></s-text-area>

        <!-- Priority Select Selector (M2) -->
        <div>
          <label style="display:block;font-size:0.875rem;font-weight:500;margin-bottom:0.5rem;color:var(--p-color-text, #202223);">Priority</label>
          <select id="intake-priority-select" class="polaris-select-custom" style="width:100%; padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;">
            <option value="low">Low</option>
            <option value="medium" selected>Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>

        <!-- Confirm Banner (C4) -->
        <div id="intake-confirmation-container" style="display:none;margin-bottom:0.25rem;"></div>

        <!-- Error Display -->
        <div id="intake-error" style="display:none;color:var(--p-color-text-critical);font-size:0.875rem;"></div>

        <!-- Actions -->
        <s-stack direction="inline" gap="small" style="margin-top:0.5rem;">
          <s-button variant="primary" id="btn-submit-intake" ${intakeSubmittingSignal.peek() ? 'disabled' : ''}>
            ${intakeSubmittingSignal.peek() ? 'Creating…' : 'Create Ticket'}
          </s-button>
          <s-button id="btn-cancel-intake" commandFor="intake-ticket-modal" command="--hide">Cancel</s-button>
        </s-stack>

      </s-stack>
    </s-modal>
  `;
}

function renderEditModal(): string {
  return `
    <s-modal id="edit-ticket-modal" heading="Edit Ticket Details">
      <s-stack gap="base">
        <!-- Error Banner -->
        <div id="edit-ticket-error" style="display:none;margin-bottom:0.5rem;">
          <s-banner tone="critical" id="edit-ticket-error-message"></s-banner>
        </div>

        <!-- Created At (Chronology override) -->
        <div style="margin-bottom:0.25rem;">
          <label style="display:block;font-size:0.875rem;font-weight:500;margin-bottom:0.5rem;color:var(--p-color-text, #202223);">Created At</label>
          <input type="datetime-local" id="edit-ticket-created-at" style="width:100%; padding:0.5rem; border:1px solid var(--p-color-border-subdued, #e1e3e5); border-radius:4px; background:var(--p-color-bg-surface); height:38px; color:var(--p-color-text); font-family:inherit;" required />
        </div>

        <!-- Contact Number -->
        <s-text-field
          id="edit-ticket-contact-number"
          label="Contact Number"
          name="contactNumber"
          placeholder="e.g. 9801234567"
          required
        ></s-text-field>

        <!-- Contact Email -->
        <s-email-field
          id="edit-ticket-contact-email"
          label="Contact Email"
          name="contactEmail"
          placeholder="e.g. support@company.com"
        ></s-email-field>

        <!-- Customer Selection -->
        <div id="edit-ticket-customer-container" style="position:relative;z-index:90;">
          <opm-autocomplete
            id="edit-ticket-customer-autocomplete"
            label="Customer Profile"
            placeholder="Search for customer..."
          ></opm-autocomplete>
        </div>

        <!-- Site Selection -->
        <div id="edit-ticket-site-container" style="position:relative;z-index:85;">
          <opm-autocomplete
            id="edit-ticket-site-autocomplete"
            label="Site / Location"
            placeholder="Search for site..."
          ></opm-autocomplete>
        </div>

        <!-- Asset Selection -->
        <div id="edit-ticket-asset-container" style="position:relative;z-index:80;">
          <opm-autocomplete
            id="edit-ticket-asset-autocomplete"
            label="Linked Printer Asset"
            placeholder="Search serial number..."
          ></opm-autocomplete>
          <div id="edit-ticket-asset-lock-banner" style="display:none;margin-top:0.5rem;">
            <s-banner tone="info">
              Selecting an existing asset automatically locks the Customer and Site fields to match the printer's registered location.
            </s-banner>
          </div>
          <div id="edit-ticket-no-assets-warning" style="display:none;margin-top:0.5rem;">
            <s-banner tone="critical">
              No registered assets found for this customer and location. Please register assets from the Devices workspace.
            </s-banner>
          </div>
        </div>

        <!-- Form Actions -->
        <div style="display:flex;justify-content:flex-end;gap:0.5rem;margin-top:1rem;">
          <s-button id="btn-cancel-edit-ticket" variant="plain">Cancel</s-button>
          <s-button variant="primary" id="btn-submit-edit-ticket">Save Changes</s-button>
        </div>
      </s-stack>
    </s-modal>
  `;
}

function resetIntakeForm() {
  intakeSelectedCustomerId = null;
  intakeSelectedSiteId = null;
  intakeIsNewCustomer = false;
  intakeIsNewSite = false;
  intakeMatchedAssetId = null;
  intakeSelectedAssetId = null;
  intakeSubmittingSignal.value = false;

  const toggle = document.getElementById('intake-new-customer-toggle') as any;
  if (toggle) toggle.checked = false;
  const newSiteToggle = document.getElementById('intake-new-site-toggle') as any;
  if (newSiteToggle) newSiteToggle.checked = false;

  const autocomplete = document.getElementById('intake-customer-autocomplete') as any;
  if (autocomplete && autocomplete.clear) autocomplete.clear();

  const siteAutocomplete = document.getElementById('intake-site-autocomplete') as any;
  if (siteAutocomplete && siteAutocomplete.clear) siteAutocomplete.clear();

  const existingAssetAutocomplete = document.getElementById('intake-existing-asset-autocomplete') as any;
  if (existingAssetAutocomplete && existingAssetAutocomplete.clear) existingAssetAutocomplete.clear();

  const fieldIds = [
    'intake-new-customer-name',
    'intake-contact-number', 'intake-contact-email', 'intake-site-name',
    'intake-new-site-name',
    'intake-existing-contact-number', 'intake-existing-contact-email',
    'intake-serial-number', 'intake-description'
  ];
  fieldIds.forEach(id => {
    const el = document.getElementById(id) as any;
    if (el) el.value = '';
  });

  intakeSelectedModelId = null;
  const modelAutocomplete = document.getElementById('intake-model-autocomplete') as any;
  if (modelAutocomplete && modelAutocomplete.clear) modelAutocomplete.clear();

  const newAssetToggle = document.getElementById('intake-new-asset-toggle') as any;
  if (newAssetToggle) {
    newAssetToggle.checked = false;
    newAssetToggle.disabled = false;
  }
  const newAssetToggleContainer = document.getElementById('intake-new-asset-toggle-container');
  if (newAssetToggleContainer) {
    newAssetToggleContainer.style.display = 'block';
  }

  // Reset priority select dropdown
  const prioritySelect = document.getElementById('intake-priority-select') as HTMLSelectElement;
  if (prioritySelect) prioritySelect.value = 'medium';

  // Hide/show sections to defaults
  const autocompleteContainer = document.getElementById('intake-customer-autocomplete-container');
  const newCustomerNameContainer = document.getElementById('intake-new-customer-name-container');
  const newCustSection = document.getElementById('intake-new-customer-section');
  const existingCustSection = document.getElementById('intake-existing-customer-section');
  const siteAutocompleteContainer = document.getElementById('intake-site-autocomplete-container');
  const newSiteNameContainer = document.getElementById('intake-new-site-name-container');

  const existingAssetContainer = document.getElementById('intake-existing-asset-autocomplete-container');
  const newAssetSerialContainer = document.getElementById('intake-new-asset-serial-container');
  const newAssetModelContainer = document.getElementById('intake-new-asset-model-container');

  if (autocompleteContainer) autocompleteContainer.style.display = 'block';
  if (newCustomerNameContainer) newCustomerNameContainer.style.display = 'none';
  if (newCustSection) newCustSection.style.display = 'none';
  if (existingCustSection) existingCustSection.style.display = 'none';
  if (siteAutocompleteContainer) siteAutocompleteContainer.style.display = 'block';
  if (newSiteNameContainer) newSiteNameContainer.style.display = 'none';

  if (existingAssetContainer) existingAssetContainer.style.display = 'block';
  if (newAssetSerialContainer) newAssetSerialContainer.style.display = 'none';
  if (newAssetModelContainer) newAssetModelContainer.style.display = 'none';

  // Hide confirmation container
  const confirmContainer = document.getElementById('intake-confirmation-container');
  if (confirmContainer) {
    confirmContainer.style.display = 'none';
    confirmContainer.innerHTML = '';
  }

  // Reset submit button text
  const submitBtnEl = document.getElementById('btn-submit-intake') as any;
  if (submitBtnEl) {
    submitBtnEl.removeAttribute('disabled');
    submitBtnEl.textContent = 'Create Ticket';
  }

  // Hide error
  const noAssetsWarning = document.getElementById('intake-no-assets-warning');
  if (noAssetsWarning) noAssetsWarning.style.display = 'none';

  const errorEl = document.getElementById('intake-error');
  if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
}

async function searchAssetsForAutocomplete(query: string): Promise<Array<{ id: string; label: string }>> {
  const h = getAuthHeaders();
  let url = `/api/v1/assets?limit=100&search=${encodeURIComponent(query)}`;
  if (intakeSelectedCustomerId) {
    url += `&customerId=${intakeSelectedCustomerId}`;
  }
  if (intakeSelectedSiteId) {
    url += `&siteId=${intakeSelectedSiteId}`;
  }
  try {
    const res = await fetch(url, { headers: h });
    if (res.ok) {
      const list = await res.json() as components['schemas']['Asset'][];
      const filteredList = list.filter(a => a.status !== 'retired');
      const models = deviceModelsSignal.value || [];

      const noAssetsWarning = document.getElementById('intake-no-assets-warning');
      if (noAssetsWarning) {
        if (filteredList.length === 0 && intakeSelectedCustomerId) {
          noAssetsWarning.style.display = 'block';
        } else {
          noAssetsWarning.style.display = 'none';
        }
      }

      return filteredList.map(a => {
        const m = models.find(model => model.id === a.modelId);
        const modelLabel = m ? `${m.vendor} ${m.modelName}`.trim() : 'Unknown Model';
        return {
          id: a.id,
          label: `${a.serialNumber} (${modelLabel})`
        };
      });
    }
  } catch (e) {
    console.error('Failed to search assets for autocomplete', e);
  }
  return [];
}

function setupIntakeListeners() {
  const sites = sitesSignal.value || [];

  // 1. Explicit New Customer Toggle listener
  const toggle = document.getElementById('intake-new-customer-toggle') as HTMLInputElement | null;
  if (toggle) {
    toggle.addEventListener('change', () => {
      const isNew = toggle.checked;
      intakeIsNewCustomer = isNew;

      const autocompleteContainer = document.getElementById('intake-customer-autocomplete-container');
      const newCustomerNameContainer = document.getElementById('intake-new-customer-name-container');
      const newCustSection = document.getElementById('intake-new-customer-section');
      const existingCustSection = document.getElementById('intake-existing-customer-section');
      const newSiteToggle = document.getElementById('intake-new-site-toggle') as HTMLInputElement | null;
      const newSiteToggleWrapper = newSiteToggle?.closest('div') as HTMLElement | null;
      const siteAutocompleteContainer = document.getElementById('intake-site-autocomplete-container');
      const newSiteNameContainer = document.getElementById('intake-new-site-name-container');
      const confirmContainer = document.getElementById('intake-confirmation-container');
      const submitBtnEl = document.getElementById('btn-submit-intake');

      // Clear any pending confirmation banner
      if (confirmContainer) {
        confirmContainer.style.display = 'none';
        confirmContainer.innerHTML = '';
      }
      if (submitBtnEl) submitBtnEl.textContent = 'Create Ticket';

      const newAssetToggle = document.getElementById('intake-new-asset-toggle') as any;
      if (isNew) {
        const customerAutocomplete = document.getElementById('intake-customer-autocomplete') as any;
        const typedQuery = (customerAutocomplete?.lastTypedQuery || customerAutocomplete?.query || '').trim();
        if (typedQuery) {
          const newCustNameInput = document.getElementById('intake-new-customer-name') as HTMLInputElement | null;
          if (newCustNameInput) {
            newCustNameInput.value = typedQuery;
          }
        }

        if (autocompleteContainer) autocompleteContainer.style.display = 'none';
        if (newCustomerNameContainer) newCustomerNameContainer.style.display = 'block';
        if (newCustSection) newCustSection.style.display = 'block';
        if (existingCustSection) existingCustSection.style.display = 'none';
        if (newSiteToggleWrapper) newSiteToggleWrapper.style.display = 'none';
        if (newSiteToggle) newSiteToggle.checked = false;
        if (siteAutocompleteContainer) siteAutocompleteContainer.style.display = 'block';
        if (newSiteNameContainer) newSiteNameContainer.style.display = 'none';
        intakeSelectedCustomerId = null;
        intakeSelectedSiteId = null;
        intakeIsNewSite = false;
        if (newAssetToggle) {
          newAssetToggle.checked = true;
          newAssetToggle.disabled = true;
          newAssetToggle.dispatchEvent(new Event('change'));
        }
      } else {
        if (autocompleteContainer) autocompleteContainer.style.display = 'block';
        if (newCustomerNameContainer) newCustomerNameContainer.style.display = 'none';
        if (newCustSection) newCustSection.style.display = 'none';
        if (newSiteToggleWrapper) newSiteToggleWrapper.style.display = 'block';

        const autocomplete = document.getElementById('intake-customer-autocomplete') as any;
        if (autocomplete) autocomplete.clear();
        intakeSelectedCustomerId = null;
        intakeSelectedSiteId = null;
        intakeIsNewSite = false;
        if (existingCustSection) existingCustSection.style.display = 'none';
        if (newAssetToggle) {
          newAssetToggle.checked = false;
          newAssetToggle.disabled = false;
          newAssetToggle.dispatchEvent(new Event('change'));
        }
      }
    });
  }

  const newSiteToggle = document.getElementById('intake-new-site-toggle') as HTMLInputElement | null;
  if (newSiteToggle) {
    newSiteToggle.addEventListener('change', () => {
      intakeIsNewSite = newSiteToggle.checked;
      const siteAutocompleteContainer = document.getElementById('intake-site-autocomplete-container');
      const newSiteNameContainer = document.getElementById('intake-new-site-name-container');
      if (siteAutocompleteContainer) siteAutocompleteContainer.style.display = intakeIsNewSite ? 'none' : 'block';
      if (newSiteNameContainer) newSiteNameContainer.style.display = intakeIsNewSite ? 'block' : 'none';
      if (intakeIsNewSite) {
        intakeSelectedSiteId = null;
        const siteAutocomplete = document.getElementById('intake-site-autocomplete') as any;
        if (siteAutocomplete?.clear) siteAutocomplete.clear();
      }
    });
  }

  // Device Model Autocomplete setup (uses loadItems like customer search)
  const modelAutocomplete = document.getElementById('intake-model-autocomplete') as any;
  if (modelAutocomplete) {
    modelAutocomplete.loadItems = searchModelsForAutocomplete;
    modelAutocomplete.addEventListener('select', (e: CustomEvent) => {
      intakeSelectedModelId = e.detail.id;
    });
  }

  // Existing Asset Autocomplete setup
  const existingAssetAutocomplete = document.getElementById('intake-existing-asset-autocomplete') as any;
  if (existingAssetAutocomplete) {
    existingAssetAutocomplete.loadItems = searchAssetsForAutocomplete;
    existingAssetAutocomplete.addEventListener('select', (e: CustomEvent) => {
      const assetId = e.detail.id;
      intakeSelectedAssetId = assetId;

      if (!assetId) return;

      const h = getAuthHeaders();
      fetch(`/api/v1/assets/${assetId}`, { headers: h })
        .then(res => {
          if (res.ok) return res.json();
          throw new Error('Failed to fetch selected asset details');
        })
        .then(asset => {
          if (asset) {
            isPrepopulatingIntakeAsset = true;
            try {
              // Pre-populate customer and site based on the selected asset
              const newCustomerToggle = document.getElementById('intake-new-customer-toggle') as HTMLInputElement | null;
              if (newCustomerToggle) {
                newCustomerToggle.checked = false;
                newCustomerToggle.dispatchEvent(new Event('change'));
              }

              const customer = allCustomersSignal.value.find(c => c.id === asset.customerId);
              const customerAutocomplete = document.getElementById('intake-customer-autocomplete') as any;
              if (customerAutocomplete && customer) {
                customerAutocomplete.items = [{ id: customer.id, label: customer.name }];
                customerAutocomplete.selectedId = customer.id;
                intakeSelectedCustomerId = customer.id;
                intakeIsNewCustomer = false;

                const site = (sitesSignal.value || []).find(s => s.id === asset.siteId);
                const siteAutocompleteEl = document.getElementById('intake-site-autocomplete') as any;
                if (siteAutocompleteEl && site) {
                  const customerSites = (sitesSignal.value || []).filter(s => s.customerId === customer.id);
                  siteAutocompleteEl.items = customerSites.map(s => ({ id: s.id, label: s.name }));
                  siteAutocompleteEl.selectedId = site.id;
                  intakeSelectedSiteId = site.id;
                  intakeIsNewSite = false;

                  const existingCustSection = document.getElementById('intake-existing-customer-section');
                  if (existingCustSection) existingCustSection.style.display = 'block';
                }
              }
            } finally {
              isPrepopulatingIntakeAsset = false;
            }
          }
        })
        .catch(err => {
          console.error('Failed to resolve selected asset details:', err);
        });
    });
  }

  // intake-new-asset-toggle change listener to switch active inputs
  const newAssetToggle = document.getElementById('intake-new-asset-toggle') as HTMLInputElement | null;
  if (newAssetToggle) {
    newAssetToggle.addEventListener('change', () => {
      const isNewAsset = newAssetToggle.checked;
      const existingAssetContainer = document.getElementById('intake-existing-asset-autocomplete-container');
      const newAssetSerialContainer = document.getElementById('intake-new-asset-serial-container');
      const newAssetModelContainer = document.getElementById('intake-new-asset-model-container');

      if (existingAssetContainer) existingAssetContainer.style.display = isNewAsset ? 'none' : 'block';
      if (newAssetSerialContainer) newAssetSerialContainer.style.display = isNewAsset ? 'block' : 'none';
      if (newAssetModelContainer) newAssetModelContainer.style.display = isNewAsset ? 'block' : 'none';

      if (isNewAsset) {
        intakeSelectedAssetId = null;
        if (existingAssetAutocomplete && existingAssetAutocomplete.clear) {
          existingAssetAutocomplete.clear();
        }
      } else {
        intakeSelectedModelId = null;
        if (modelAutocomplete && modelAutocomplete.clear) {
          modelAutocomplete.clear();
        }
        const serialEl = document.getElementById('intake-serial-number') as HTMLInputElement | null;
        if (serialEl) serialEl.value = '';
      }
    });
  }

  // 2. Existing Customer Autocomplete items
  const autocomplete = document.getElementById('intake-customer-autocomplete') as any;
  if (autocomplete) {
    autocomplete.loadItems = searchCustomersForAutocomplete;

    // On customer selected from autocomplete
    autocomplete.addEventListener('select', (e: CustomEvent) => {
      const customerId = e.detail.id;
      intakeSelectedCustomerId = customerId;
      intakeIsNewCustomer = false;

      // Clear any currently selected asset when changing the customer, unless prepopulating
      if (!isPrepopulatingIntakeAsset) {
        intakeSelectedAssetId = null;
        if (existingAssetAutocomplete && existingAssetAutocomplete.clear) {
          existingAssetAutocomplete.clear();
        }
      }

      // Show existing customer section, hide new customer section
      const newCustSection = document.getElementById('intake-new-customer-section');
      const existingCustSection = document.getElementById('intake-existing-customer-section');
      if (newCustSection) newCustSection.style.display = 'none';
      if (existingCustSection) existingCustSection.style.display = 'block';

      // Populate site autocomplete for this customer
      const siteAutocomplete = document.getElementById('intake-site-autocomplete') as any;
      if (siteAutocomplete) {
        const customerSites = sites.filter(s => s.customerId === customerId);
        const siteItems = customerSites.map(s => ({ id: s.id, label: s.name }));
        siteAutocomplete.items = siteItems;
        if (!isPrepopulatingIntakeAsset) {
          siteAutocomplete.clear();
          intakeSelectedSiteId = null;
        }
      }
    });
  }

  // 3. Reset confirmation banner if user types new details
  const fieldsToResetConfirm = ['intake-new-customer-name', 'intake-contact-number', 'intake-site-name', 'intake-new-site-name'];
  fieldsToResetConfirm.forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('input', () => {
        const confirmContainer = document.getElementById('intake-confirmation-container');
        const submitBtnEl = document.getElementById('btn-submit-intake');
        if (confirmContainer && confirmContainer.style.display === 'block') {
          confirmContainer.style.display = 'none';
          confirmContainer.innerHTML = '';
          if (submitBtnEl) submitBtnEl.textContent = 'Create Ticket';
        }
      });
    }
  });

  // Handle site autocomplete selection
  const siteAutocomplete = document.getElementById('intake-site-autocomplete') as any;
  if (siteAutocomplete) {
    siteAutocomplete.addEventListener('select', (e: CustomEvent) => {
      intakeSelectedSiteId = e.detail.id;
      // Clear any currently selected asset when changing the site
      intakeSelectedAssetId = null;
      if (existingAssetAutocomplete && existingAssetAutocomplete.clear) {
        existingAssetAutocomplete.clear();
      }
    });
  }

  // Handle serial number auto-fill and validation
  const serialInput = document.getElementById('intake-serial-number') as HTMLInputElement | null;
  if (serialInput) {
    serialInput.addEventListener('input', () => {
      const serial = serialInput.value.trim();
      const asset = assetsListSignal.value.find(a => a.serialNumber.toLowerCase() === serial.toLowerCase());
      const newAssetToggleContainer = document.getElementById('intake-new-asset-toggle-container');
      const newAssetToggle = document.getElementById('intake-new-asset-toggle') as any;
      const modelAutocompleteEl = document.getElementById('intake-model-autocomplete') as any;
      const newCustomerToggle = document.getElementById('intake-new-customer-toggle') as HTMLInputElement | null;

      if (asset) {
        isPrepopulatingIntakeAsset = true;
        try {
          intakeMatchedAssetId = asset.id;
          intakeSelectedAssetId = asset.id;

          const existingAssetAutocomplete = document.getElementById('intake-existing-asset-autocomplete') as any;
          if (existingAssetAutocomplete) {
            const models = deviceModelsSignal.value || [];
            const m = models.find(model => model.id === asset.modelId);
            const modelLabel = m ? `${m.vendor} ${m.modelName}`.trim() : 'Unknown Model';
            existingAssetAutocomplete.items = [{ id: asset.id, label: `${asset.serialNumber} (${modelLabel})` }];
            existingAssetAutocomplete.selectedId = asset.id;
          }

          if (modelAutocompleteEl) {
            modelAutocompleteEl.selectedId = asset.modelId;
            intakeSelectedModelId = asset.modelId;
          }

          if (newCustomerToggle) {
            newCustomerToggle.checked = false;
            newCustomerToggle.dispatchEvent(new Event('change'));
          }

          const customer = allCustomersSignal.value.find(c => c.id === asset.customerId);
          const customerAutocomplete = document.getElementById('intake-customer-autocomplete') as any;
          if (customerAutocomplete && customer) {
            customerAutocomplete.items = [{ id: customer.id, label: customer.name }];
            customerAutocomplete.selectedId = customer.id;
            intakeSelectedCustomerId = customer.id;
            intakeIsNewCustomer = false;

            const site = (sitesSignal.value || []).find(s => s.id === asset.siteId);
            const siteAutocompleteEl = document.getElementById('intake-site-autocomplete') as any;
            if (siteAutocompleteEl && site) {
              const customerSites = (sitesSignal.value || []).filter(s => s.customerId === customer.id);
              siteAutocompleteEl.items = customerSites.map(s => ({ id: s.id, label: s.name }));
              siteAutocompleteEl.selectedId = site.id;
              intakeSelectedSiteId = site.id;
              intakeIsNewSite = false;

              const existingCustSection = document.getElementById('intake-existing-customer-section');
              if (existingCustSection) existingCustSection.style.display = 'block';
            }
          }

          if (newAssetToggle) {
            newAssetToggle.checked = false;
            newAssetToggle.dispatchEvent(new Event('change'));
          }
          if (newAssetToggleContainer) {
            newAssetToggleContainer.style.display = 'none';
          }

          showToast("Existing asset found! Pre-populated details.", "success");
        } finally {
          isPrepopulatingIntakeAsset = false;
        }
      } else {
        intakeMatchedAssetId = null;
        intakeSelectedAssetId = null;
        const existingAssetAutocomplete = document.getElementById('intake-existing-asset-autocomplete') as any;
        if (existingAssetAutocomplete && existingAssetAutocomplete.clear) {
          existingAssetAutocomplete.clear();
        }

        if (modelAutocompleteEl && modelAutocompleteEl.clear) modelAutocompleteEl.clear();
        intakeSelectedModelId = null;

        const customerAutocomplete = document.getElementById('intake-customer-autocomplete') as any;
        if (customerAutocomplete) customerAutocomplete.clear();
        intakeSelectedCustomerId = null;

        const siteAutocompleteEl = document.getElementById('intake-site-autocomplete') as any;
        if (siteAutocompleteEl) siteAutocompleteEl.clear();
        intakeSelectedSiteId = null;

        if (newAssetToggleContainer) {
          newAssetToggleContainer.style.display = 'block';
        }
        if (newAssetToggle) {
          newAssetToggle.checked = true;
          newAssetToggle.dispatchEvent(new Event('change'));
        }
      }
    });
  }

  // Open intake modal
  const createBtn = document.getElementById('btn-open-intake-modal');
  if (createBtn) {
    createBtn.addEventListener('click', () => {
      resetIntakeForm();
      const modal = document.getElementById('intake-ticket-modal') as any;
      if (modal && typeof modal.showOverlay === 'function') modal.showOverlay();
    });
  }

  // Submit intake
  const submitBtn = document.getElementById('btn-submit-intake');
  if (submitBtn) {
    submitBtn.addEventListener('click', async () => {
      const errorEl = document.getElementById('intake-error');
      const showError = (msg: string) => {
        if (errorEl) { errorEl.style.display = 'block'; errorEl.textContent = msg; }
      };
      const clearError = () => {
        if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
      };
      clearError();

      const isNew = intakeIsNewCustomer;
      const custId = intakeSelectedCustomerId;

      // Get customer name
      const customerNameRaw = isNew
        ? (document.getElementById('intake-new-customer-name') as HTMLInputElement)?.value?.trim() || ''
        : '';

      // Validate customer
      if (!isNew && !custId) {
        showError('Please select a customer or check "This is a new customer profile".');
        return;
      }
      if (isNew && !customerNameRaw) {
        showError('Customer name is required.');
        return;
      }

      // Gather field values based on new vs existing customer
      let contactNumber = '';
      let contactEmail = '';
      let siteId = '';
      let siteNameRaw = '';

      if (isNew) {
        contactNumber = (document.getElementById('intake-contact-number') as any)?.value?.trim() || '';
        contactEmail = (document.getElementById('intake-contact-email') as any)?.value?.trim() || '';
        siteNameRaw = (document.getElementById('intake-site-name') as any)?.value?.trim() || '';

        if (!contactNumber) { showError('Contact number is required for new customers.'); return; }
        if (!siteNameRaw) { showError('Primary site name is required for new customers.'); return; }
      } else {
        contactNumber = (document.getElementById('intake-existing-contact-number') as any)?.value?.trim() || '';
        contactEmail = (document.getElementById('intake-existing-contact-email') as any)?.value?.trim() || '';
        if (intakeIsNewSite) {
          siteNameRaw = (document.getElementById('intake-new-site-name') as any)?.value?.trim() || '';
        } else {
          siteId = intakeSelectedSiteId || '';
        }

        if (intakeIsNewSite && !siteNameRaw) { showError('Site name is required.'); return; }
        if (!intakeIsNewSite && !siteId) { showError('Please select a site.'); return; }
        if (!contactNumber) { showError('Contact number is required.'); return; }
      }

      let serialNumberRaw = (document.getElementById('intake-serial-number') as any)?.value?.trim() || '';
      const description = (document.getElementById('intake-description') as any)?.value?.trim() || '';

      const newAssetToggle = document.getElementById('intake-new-asset-toggle') as any;
      const createAsset = !!newAssetToggle?.checked;
      const modelAutocompleteEl = document.getElementById('intake-model-autocomplete') as any;
      const modelId = intakeSelectedModelId || (modelAutocompleteEl?.selectedId) || '';

      if (createAsset) {
        if (!serialNumberRaw) {
          const randomSuffix = Math.random().toString(36).substring(2, 8).toUpperCase();
          serialNumberRaw = `PENDING-SERIAL-NUMBER-${randomSuffix}`;
        }
        if (!modelId) {
          showError('Please select a device model to register the new asset.');
          return;
        }
      } else {
        if (!intakeSelectedAssetId) {
          showError('Please select an existing asset serial number or check "This is a new printer asset".');
          return;
        }
      }

      if (!description) { showError('Failure description is required.'); return; }

      // Get selected priority from select selector (M2)
      const prioritySelect = document.getElementById('intake-priority-select') as HTMLSelectElement;
      const priority = prioritySelect?.value || 'medium';

      // Confirm step before auto-registration (C4)
      if (isNew) {
        const confirmContainer = document.getElementById('intake-confirmation-container');
        if (confirmContainer && confirmContainer.style.display !== 'block') {
          confirmContainer.innerHTML = `
            <s-banner tone="warning" title="Confirm New Profile Registration">
              <s-paragraph>
                Creating this ticket will register a new customer profile <strong>${escapeHtml(customerNameRaw)}</strong> and a primary site <strong>${escapeHtml(siteNameRaw)}</strong> in the database.
                Click "Confirm & Create Ticket" to proceed.
              </s-paragraph>
            </s-banner>
          `;
          confirmContainer.style.display = 'block';
          submitBtn.textContent = 'Confirm & Create Ticket';
          return;
        }
      }

      // Assemble TicketInput payload
      const payload: any = {
        description,
        contactNumber,
        priority,
      };

      if (isNew) {
        payload.customerId = '';
        payload.customerNameRaw = customerNameRaw;
        payload.siteNameRaw = siteNameRaw;
      } else {
        payload.customerId = custId;
        if (intakeIsNewSite) {
          payload.siteNameRaw = siteNameRaw;
        } else if (siteId) {
          payload.siteId = siteId;
        }
      }

      if (contactEmail) payload.contactEmail = contactEmail;
      if (serialNumberRaw) payload.serialNumberRaw = serialNumberRaw;

      if (createAsset) {
        payload.createAsset = true;
        payload.modelId = modelId;
      } else if (intakeSelectedAssetId) {
        payload.assetId = intakeSelectedAssetId;
      } else if (intakeMatchedAssetId) {
        payload.assetId = intakeMatchedAssetId;
      }

      // Submit
      intakeSubmittingSignal.value = true;
      submitBtn.setAttribute('disabled', 'true');
      submitBtn.textContent = 'Creating…';

      try {
        const res = await fetch('/api/v1/tickets', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });

        if (res.ok) {
          const newTicket = await res.json() as Ticket;
          const modal = document.getElementById('intake-ticket-modal') as any;
          if (modal && typeof modal.hideOverlay === 'function') modal.hideOverlay();
          resetIntakeForm();
          // Refresh customer/site lists in case a new customer or site was auto-registered
          await initAssetsSignals().catch(err => console.error('Failed to refresh assets metadata:', err));
          await refreshTickets();

          // Auto-select the newly created ticket
          const newTicketId = newTicket.id;
          selectedTicketIdSignal.value = newTicketId;
          ticketSessionsSignal.value = [];
          ticketTimelineEventsSignal.value = [];
          fetchTicketSessions(newTicketId);

          // Scroll newly created ticket row into view after a brief render delay
          setTimeout(() => {
            const viewBtn = document.querySelector(`[data-id="${newTicketId}"]`);
            if (viewBtn) {
              const rowEl = viewBtn.closest('s-table-row') || viewBtn;
              rowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 200);

          showToast('Ticket created successfully.', 'success');
        } else {
          const errBody = await res.json().catch(() => ({ error: 'Ticket creation failed' }));
          showError(errBody.error || errBody.message || 'Failed to create ticket.');
        }
      } catch (e) {
        console.error('Intake submission failed:', e);
        showError('Network error during ticket creation.');
      } finally {
        intakeSubmittingSignal.value = false;
        if (submitBtn) { submitBtn.removeAttribute('disabled'); submitBtn.textContent = 'Create Ticket'; }
      }
    });
  }
}

async function searchAssetsForEditAutocomplete(query: string): Promise<Array<{ id: string; label: string }>> {
  const h = getAuthHeaders();
  let url = `/api/v1/assets?limit=100&search=${encodeURIComponent(query)}`;
  if (editSelectedCustomerId) {
    url += `&customerId=${editSelectedCustomerId}`;
  }
  if (editSelectedSiteId) {
    url += `&siteId=${editSelectedSiteId}`;
  }
  try {
    const res = await fetch(url, { headers: h });
    if (res.ok) {
      const list = await res.json() as components['schemas']['Asset'][];
      const filteredList = list.filter(a => a.status !== 'retired');
      const models = deviceModelsSignal.value || [];

      const warningEl = document.getElementById('edit-ticket-no-assets-warning');
      if (warningEl) {
        if (filteredList.length === 0 && editSelectedCustomerId) {
          warningEl.style.display = 'block';
        } else {
          warningEl.style.display = 'none';
        }
      }

      return filteredList.map(a => {
        const m = models.find(model => model.id === a.modelId);
        const modelLabel = m ? `${m.vendor} ${m.modelName}`.trim() : 'Unknown Model';
        return {
          id: a.id,
          label: `${a.serialNumber} (${modelLabel})`
        };
      });
    }
  } catch (e) {
    console.error('Failed to search assets for edit autocomplete', e);
  }
  return [];
}

function setupEditModalListeners() {
  const customerAutocomplete = document.getElementById('edit-ticket-customer-autocomplete') as any;
  const siteAutocomplete = document.getElementById('edit-ticket-site-autocomplete') as any;
  const assetAutocomplete = document.getElementById('edit-ticket-asset-autocomplete') as any;
  const lockBanner = document.getElementById('edit-ticket-asset-lock-banner');
  const noAssetsWarning = document.getElementById('edit-ticket-no-assets-warning');

  // Load items hook for customer and asset search
  if (customerAutocomplete) {
    customerAutocomplete.loadItems = searchCustomersForAutocomplete;
    customerAutocomplete.addEventListener('select', (e: CustomEvent) => {
      const custId = e.detail.id;
      editSelectedCustomerId = custId;
      if (!isPrepopulatingEditAsset) {
        editSelectedSiteId = null;
        editSelectedAssetId = null;
      }

      // Reset site and asset fields
      if (siteAutocomplete) {
        if (!isPrepopulatingEditAsset) {
          siteAutocomplete.clear?.();
        }
        if (custId) {
          const customerSites = (sitesSignal.value || []).filter(s => s.customerId === custId);
          siteAutocomplete.items = customerSites.map(s => ({ id: s.id, label: s.name }));
        } else {
          siteAutocomplete.items = [];
        }
      }
      if (assetAutocomplete && !isPrepopulatingEditAsset) {
        assetAutocomplete.clear?.();
      }
      if (noAssetsWarning) noAssetsWarning.style.display = 'none';
    });

    customerAutocomplete.addEventListener('clear', () => {
      editSelectedCustomerId = null;
      editSelectedSiteId = null;
      editSelectedAssetId = null;
      if (siteAutocomplete) siteAutocomplete.clear?.();
      if (assetAutocomplete) assetAutocomplete.clear?.();
      if (noAssetsWarning) noAssetsWarning.style.display = 'none';
    });
  }

  if (siteAutocomplete) {
    siteAutocomplete.addEventListener('select', (e: CustomEvent) => {
      editSelectedSiteId = e.detail.id;
      if (!isPrepopulatingEditAsset) {
        editSelectedAssetId = null;
        if (assetAutocomplete) {
          assetAutocomplete.clear?.();
        }
      }
      if (noAssetsWarning) noAssetsWarning.style.display = 'none';
    });

    siteAutocomplete.addEventListener('clear', () => {
      editSelectedSiteId = null;
      editSelectedAssetId = null;
      if (assetAutocomplete) assetAutocomplete.clear?.();
      if (noAssetsWarning) noAssetsWarning.style.display = 'none';
    });
  }

  if (assetAutocomplete) {
    assetAutocomplete.loadItems = searchAssetsForEditAutocomplete;
    assetAutocomplete.addEventListener('select', (e: CustomEvent) => {
      const assetId = e.detail.id;
      editSelectedAssetId = assetId;

      if (!assetId) {
        // Clear locked inputs
        if (customerAutocomplete) customerAutocomplete.removeAttribute('disabled');
        if (siteAutocomplete) siteAutocomplete.removeAttribute('disabled');
        if (lockBanner) lockBanner.style.display = 'none';
        return;
      }

      // Fetch asset details to lock site and customer
      const h = getAuthHeaders();
      fetch(`/api/v1/assets/${assetId}`, { headers: h })
        .then(res => {
          if (res.ok) return res.json();
          throw new Error('Failed to resolve asset info');
        })
        .then(asset => {
          if (asset) {
            isPrepopulatingEditAsset = true;
            try {
              // Pre-populate customer
              const customer = allCustomersSignal.value.find(c => c.id === asset.customerId);
              if (customerAutocomplete && customer) {
                customerAutocomplete.items = [{ id: customer.id, label: customer.name }];
                customerAutocomplete.selectedId = customer.id;
                editSelectedCustomerId = customer.id;
                customerAutocomplete.setAttribute('disabled', 'true');
              }

              // Pre-populate site
              const site = (sitesSignal.value || []).find(s => s.id === asset.siteId);
              if (siteAutocomplete) {
                const customerSites = (sitesSignal.value || []).filter(s => s.customerId === asset.customerId);
                siteAutocomplete.items = customerSites.map(s => ({ id: s.id, label: s.name }));
                if (site) {
                  siteAutocomplete.selectedId = site.id;
                  editSelectedSiteId = site.id;
                } else {
                  siteAutocomplete.clear?.();
                  editSelectedSiteId = null;
                }
                siteAutocomplete.setAttribute('disabled', 'true');
              }

              if (lockBanner) lockBanner.style.display = 'block';
            } finally {
              isPrepopulatingEditAsset = false;
            }
          }
        })
        .catch(err => {
          console.error('Failed to resolve asset during edit selection', err);
        });
    });

    assetAutocomplete.addEventListener('clear', () => {
      editSelectedAssetId = null;
      if (customerAutocomplete) customerAutocomplete.removeAttribute('disabled');
      if (siteAutocomplete) siteAutocomplete.removeAttribute('disabled');
      if (lockBanner) lockBanner.style.display = 'none';
    });
  }

  // Cancel button
  const cancelBtn = document.getElementById('btn-cancel-edit-ticket');
  if (cancelBtn) {
    cancelBtn.addEventListener('click', () => {
      const modal = document.getElementById('edit-ticket-modal') as any;
      if (modal && typeof modal.hideOverlay === 'function') modal.hideOverlay();
    });
  }

  // Save changes button
  const submitBtn = document.getElementById('btn-submit-edit-ticket');
  if (submitBtn) {
    submitBtn.addEventListener('click', async () => {
      const ticket = getSelectedTicket();
      if (!ticket) return;

      const errorEl = document.getElementById('edit-ticket-error');
      const errorMessageEl = document.getElementById('edit-ticket-error-message');
      const showError = (msg: string) => {
        if (errorEl && errorMessageEl) {
          errorEl.style.display = 'block';
          errorMessageEl.textContent = msg;
        }
      };

      const createdAtInput = document.getElementById('edit-ticket-created-at') as HTMLInputElement | null;
      const createdAtRaw = createdAtInput?.value || '';
      if (!createdAtRaw) {
        showError('Created At timestamp is required.');
        return;
      }

      const contactNumber = (document.getElementById('edit-ticket-contact-number') as any)?.value?.trim() || '';
      if (!contactNumber) {
        showError('Contact Number is required.');
        return;
      }

      const contactEmail = (document.getElementById('edit-ticket-contact-email') as any)?.value?.trim() || '';

      if (!editSelectedCustomerId) {
        showError('Please select a Customer.');
        return;
      }

      if (!editSelectedSiteId) {
        showError('Please select a Site/Location.');
        return;
      }

      // Format date correctly to ISO date-time
      const createdAt = new Date(createdAtRaw).toISOString();

      submitBtn.setAttribute('loading', 'true');
      submitBtn.setAttribute('disabled', 'true');

      const payload = {
        createdAt,
        contactNumber,
        contactEmail: contactEmail || null,
        customerId: editSelectedCustomerId,
        siteId: editSelectedSiteId,
        assetId: editSelectedAssetId || null,
      };

      try {
        const res = await fetch(`/api/v1/tickets/${ticket.id}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });

        if (res.ok) {
          showToast('Ticket updated successfully.', 'success');
          const modal = document.getElementById('edit-ticket-modal') as any;
          if (modal && typeof modal.hideOverlay === 'function') modal.hideOverlay();

          // Re-trigger tickets search/poll logic by refreshing the data
          if (typeof refreshTickets === 'function') {
            void refreshTickets();
          }
          // Also fetch active ticket sessions/timeline to redraw detail panel
          selectedTicketIdSignal.value = ticket.id;
          void fetchTicketSessions(ticket.id);
        } else {
          const errObj = await res.json() as any;
          showError(errObj?.error || 'Failed to update ticket.');
        }
      } catch (err) {
        console.error('Failed to submit edit ticket form', err);
        showError('Network error occurred. Please try again.');
      } finally {
        submitBtn.removeAttribute('loading');
        submitBtn.removeAttribute('disabled');
      }
    });
  }
}

// ─── Status Filter Tabs ────────────────────────────────────────────────────────

function renderFilterTabs(): string {
  const activeFilter = ticketsStatusFilterSignal.peek();
  const counts = ticketsStatusCountsSignal.peek();

  const tabs = [
    { key: 'open', label: 'Open' },
    { key: 'assigned', label: 'Awaiting Visit' },
    { key: 'in_progress', label: 'Service Active' },
    { key: 'resolved', label: 'Resolved' },
    { key: 'closed', label: 'Closed' },
    { key: 'all', label: 'All' },
  ];

  return tabs.map(tab => {
    const isActive = activeFilter === tab.key;
    const count = counts[tab.key] || 0;
    return `
      <button
        class="ticket-filter-tab ${isActive ? 'ticket-filter-tab--active' : ''}"
        data-filter="${tab.key}"
        id="ticket-filter-${tab.key}"
      >
        ${tab.label}
        <span class="ticket-filter-count">${count}</span>
      </button>
    `;
  }).join('');
}

// ─── Page Renderer ─────────────────────────────────────────────────────────────

export function renderTicketsPage(): string {
  const role = activeRoleSignal.peek();
  const isAuthorized = role === 'superadmin' || role === 'admin' || role === 'manager' || role === 'technician' || role === 'viewer';
  const canCreate = role !== 'viewer';

  if (!isAuthorized) {
    return `
      <s-page heading="Access Denied" inlineSize="large">
        <s-section>
          <s-box padding="base" background="surface" border="base" borderRadius="base">
            <s-banner tone="critical">
              You do not have permissions to access the Tickets workspace. Contact your administrator.
            </s-banner>
          </s-box>
        </s-section>
      </s-page>
    `;
  }

  return `
    <style>
      .tickets-workspace {
        display: grid;
        grid-template-columns: minmax(0, 2fr) minmax(380px, 1fr);
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }

      .ticket-list-panel {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
        border-right: 1px solid var(--p-color-border-subdued, #e1e3e5);
      }

      .ticket-list-header {
        padding: 1.25rem 1.25rem 0.75rem;
        border-bottom: 1px solid var(--p-color-border-subdued, #e1e3e5);
        background: var(--p-color-bg-surface, #ffffff);
      }

      .ticket-filter-bar {
        display: flex;
        gap: 0.25rem;
        flex-wrap: wrap;
        padding: 0.75rem 1.25rem;
        background: var(--p-color-bg-surface, #ffffff);
        border-bottom: 1px solid var(--p-color-border-subdued, #e1e3e5);
      }

      .ticket-filter-tab {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.3rem 0.65rem;
        font-size: 0.8rem;
        font-weight: 500;
        border: 1px solid var(--p-color-border-subdued, #c4cdd5);
        border-radius: var(--p-border-radius-200, 8px);
        background: transparent;
        color: var(--p-color-text-subdued, #6d7175);
        cursor: pointer;
        transition: all 0.15s ease;
        font-family: inherit;
      }

      .ticket-filter-tab:hover {
        background: var(--p-color-bg-action-hover, rgba(0,0,0,0.05));
        color: var(--p-color-text, #202223);
      }

      .ticket-filter-tab--active {
        background: var(--p-color-bg-surface-selected, #e4e6eb);
        color: var(--p-color-text, #202223);
        border-color: var(--p-color-border-accent, #008060);
        font-weight: 600;
      }

      .ticket-filter-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        font-size: 0.7rem;
        font-weight: 600;
        background: var(--p-color-bg-surface-secondary, #f1f2f4);
        border-radius: 9px;
        padding: 0 4px;
      }

      .ticket-filter-tab--active .ticket-filter-count {
        background: var(--p-color-bg-action-primary, #008060);
        color: white;
      }

      .ticket-list-body {
        flex: 1;
        overflow-y: auto;
        background: var(--p-color-bg-app, #f6f6f7);
      }

      /* Active Row Light Blue Highlight & Smooth Selection Animation */
      s-table-row {
        transition: background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1),
                    box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1),
                    outline-color 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
        box-sizing: border-box !important;
        position: relative !important;
      }

      s-table-row:hover {
        background-color: rgba(43, 130, 251, 0.04) !important;
      }

      s-table-row.ticket-row-active,
      s-table-row[data-selected="true"] {
        background-color: #f0f7ff !important;
        box-shadow: inset 0 0 0 2px #2b82fb, inset 0 0 0 3px rgba(43, 130, 251, 0.2) !important;
      }

      s-table-row.ticket-row-active s-table-cell,
      s-table-row[data-selected="true"] s-table-cell {
        background-color: transparent !important;
      }

      .ticket-detail-panel {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
        background: var(--p-color-bg-surface, #ffffff);
      }

      .ticket-detail-empty {
        background: var(--p-color-bg-app, #f6f6f7);
      }

      /* Mobile overlay detail */
      .ticket-detail-wrapper {
        display: block;
        overflow-y: scroll;
        padding-y: 2rem;
      }

      .ticket-detail-wrapper.fullscreen-mode {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 99999 !important;
        background: var(--p-color-bg-surface, #ffffff) !important;
        display: flex !important;
        flex-direction: column !important;
      }

      @media (max-width: 768px) {
        .tickets-workspace {
          grid-template-columns: 1fr;
        }

        .ticket-detail-wrapper {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 9000;
          background: var(--p-color-bg-surface, #ffffff);
        }

        .ticket-detail-wrapper.detail-visible {
          display: flex;
          flex-direction: column;
        }

        .ticket-detail-mobile-header {
          display: flex !important;
          align-items: center;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid var(--p-color-border-subdued, #e1e3e5);
          gap: 0.5rem;
        }
      }

      @media (min-width: 769px) {
        .ticket-detail-mobile-header {
          display: none !important;
        }
      }

      .opm-tooltip-container {
        position: relative;
        display: inline-block;
      }
      .opm-tooltip-text {
        visibility: hidden;
        width: 250px;
        background-color: var(--p-color-bg-inverse, #1f2124);
        color: var(--p-color-text-inverse, #ffffff);
        text-align: center;
        border-radius: var(--p-border-radius-200, 8px);
        padding: 0.5rem 0.75rem;
        position: absolute;
        z-index: 1000;
        bottom: 125%;
        right: 0;
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
        box-shadow: var(--p-shadow-400, 0 16px 20px rgba(0,0,0,0.15));
        font-size: 0.75rem;
        line-height: 1.4;
        white-space: normal;
        pointer-events: none;
      }
      .opm-tooltip-container:hover .opm-tooltip-text {
        visibility: visible;
        opacity: 1;
      }
    </style>

    <div style="display:flex;flex-direction:column;height:100%;">
      <!-- Page Header -->
      <div style="padding:1.25rem 1.5rem 0;background:var(--p-color-bg-surface, #ffffff);border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);">
        <s-stack direction="inline" gap="base" align="center" wrap>
          <s-heading element="h1" style="font-size:1.25rem;font-weight:700;margin:0;">Support Tickets</s-heading>
          <s-badge tone="info" id="ticket-total-badge">0 total</s-badge>
          <div style="margin-left:auto; margin-bottom: 10px;">
            <s-button href="#ticket-suggestions" variant="plain" id="btn-open-ticket-suggestions">Ticket Suggestions</s-button>
            ${canCreate ? `
              <s-button variant="primary" id="btn-open-intake-modal">+ Create Ticket</s-button>
            ` : `
              <div class="opm-tooltip-container">
                <s-button variant="primary" id="btn-open-intake-modal" disabled>+ Create Ticket</s-button>
                <div class="opm-tooltip-text">
                  Viewers have read-only access to support tickets.
                </div>
              </div>
            `}
          </div>
        </s-stack>
      </div>

      <!-- Filter Tabs -->
      <div class="ticket-filter-bar" id="ticket-filter-bar-container"></div>

      <!-- Split Pane -->
      <div class="tickets-workspace">
        <!-- Left: Ticket List -->
        <div class="ticket-list-panel">
          <div class="ticket-list-body" style="padding:0.75rem;position:relative;">
            <!-- Batch Operations Bar -->
            <div id="tickets-batch-bar" style="display:none;background:var(--p-color-bg-surface-selected, #e2f1ea);border:1px solid var(--p-color-border-success, #008060);border-radius:4px;padding:0.5rem 1rem;margin-bottom:0.75rem;align-items:center;justify-content:space-between;gap:1rem;flex-wrap:wrap;z-index: 5;">
              <s-stack direction="inline" gap="base" alignment="center">
                <s-text style="font-weight:600;"><span id="batch-selected-count">0</span> tickets selected</s-text>
              </s-stack>
              <s-stack direction="inline" gap="small">
                <s-button id="btn-batch-assign" variant="primary" size="slim">Assign Selected</s-button>
                <s-button id="btn-batch-close" variant="critical" size="slim">Close Selected</s-button>
              </s-stack>
            </div>
            <div id="tickets-loading-spinner" style="display: none; position:absolute;inset:0;align-items:center;justify-content:center;background:rgba(255,255,255,0.7);z-index:10;border-radius:4px;">
              <s-spinner size="large"></s-spinner>
            </div>
            <opm-data-table
              id="tickets-data-table"
              searchable="true"
              search-placeholder="Search by ticket #, customer, technician, or description…"
              paginated="true"
              page-size="15"
              selectable="${(role === 'superadmin' || role === 'admin' || role === 'manager') ? 'true' : 'false'}"
              empty-message="No tickets match the current filter."
            ></opm-data-table>
          </div>
        </div>

        <!-- Right: Detail Panel -->
        <div class="ticket-detail-wrapper" id="ticket-detail-wrapper"></div>
      </div>
    </div>

    <div id="assign-modal-outer-container"></div>
    <div id="batch-assign-modal-outer-container"></div>
    <div id="intake-modal-outer-container"></div>
    <div id="edit-ticket-modal-outer-container"></div>
  `;
}

// ─── Event Listeners ───────────────────────────────────────────────────────────

export function setupTicketsPageListeners() {
  // Restore signals from sessionStorage if this is a fresh mount with saved state
  try {
    const savedFilter = sessionStorage.getItem('tickets_status_filter');
    const savedPage = sessionStorage.getItem('tickets_page');
    const savedPageSize = sessionStorage.getItem('tickets_page_size');
    if (savedFilter !== null) {
      ticketsStatusFilterSignal.value = savedFilter;
      sessionStorage.removeItem('tickets_status_filter');
    }
    if (savedPage !== null) {
      ticketsPageSignal.value = Number(savedPage) || 1;
      sessionStorage.removeItem('tickets_page');
    }
    if (savedPageSize !== null) {
      ticketsPageSizeSignal.value = Number(savedPageSize) || 15;
      sessionStorage.removeItem('tickets_page_size');
    }
  } catch (e) {
    // sessionStorage unavailable
  }

  // Trigger tickets list fetch immediately upon mount
  refreshTickets();

  const role = activeRoleSignal.peek();
  const canCreate = role !== 'viewer';

  // Populate structural layout containers dynamically to prevent router context tracking
  const filterBar = document.getElementById('ticket-filter-bar-container');
  if (filterBar) filterBar.innerHTML = renderFilterTabs();

  const detailWrapper = document.getElementById('ticket-detail-wrapper');
  if (detailWrapper) {
    const isSelected = selectedTicketIdSignal.peek();
    if (isSelected) {
      detailWrapper.innerHTML = `
        <div class="ticket-detail-mobile-header">
          <s-button id="btn-close-ticket-detail" variant="plain" size="slim">
            <s-icon type="arrow-left" size="base"></s-icon>
            Back to list
          </s-button>
        </div>
        ${renderDetailPanel()}
      `;
      detailWrapper.classList.add('detail-visible');
    } else {
      detailWrapper.innerHTML = `
        <div class="ticket-detail-mobile-header">
          <s-button id="btn-close-ticket-detail" variant="plain" size="slim">
            <s-icon type="arrow-left" size="base"></s-icon>
            Back to list
          </s-button>
        </div>
        ${renderEmptyDetailPanel()}
      `;
      detailWrapper.classList.remove('detail-visible');
    }
  }

  const assignModalOuter = document.getElementById('assign-modal-outer-container');
  if (assignModalOuter) assignModalOuter.innerHTML = renderAssignModal();

  const batchAssignModalOuter = document.getElementById('batch-assign-modal-outer-container');
  if (batchAssignModalOuter) batchAssignModalOuter.innerHTML = renderBatchAssignModal();

  if (canCreate) {
    const intakeModalOuter = document.getElementById('intake-modal-outer-container');
    if (intakeModalOuter) intakeModalOuter.innerHTML = renderIntakeModal();
  }

  const isManagerOrAdmin = role === 'superadmin' || role === 'admin' || role === 'manager';
  if (isManagerOrAdmin) {
    const editModalOuter = document.getElementById('edit-ticket-modal-outer-container');
    if (editModalOuter) {
      editModalOuter.innerHTML = renderEditModal();
      setupEditModalListeners();
    }
  }

  // Configure data table columns (set once, items updated reactively)
  const table = document.getElementById('tickets-data-table') as any;
  if (table) {
    table.columns = [
      {
        key: 'ticketNumber',
        header: 'Ticket #',
        sortable: true,
        sortType: 'string',
        render: (row: any) => `<strong>${row.ticketNumber}</strong>`,
      },
      {
        key: 'customerName',
        header: 'Customer',
        sortable: true,
        sortType: 'string',
        render: (row: any) => escapeHtml(getCustomerName(row.customerId)),
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        sortType: 'string',
        render: (row: any) => {
          const effectiveStatus = getEffectiveTicketStatus(row as Ticket);
          return `<s-badge tone="${statusBadgeTone(effectiveStatus)}">${statusLabel(effectiveStatus)}</s-badge>`;
        },
      },
      {
        key: 'priority',
        header: 'Priority',
        sortable: true,
        sortType: 'string',
        render: (row: any) =>
          `<s-badge tone="${priorityBadgeTone(row.priority)}">${priorityLabel(row.priority)}</s-badge>`,
      },
      {
        key: 'assignedTechs',
        header: 'Assigned To',
        sortable: false,
        render: (row: any) => {
          const assignments = getAssignedTechIds(row as Ticket);
          if (assignments.length === 0) {
            return `<s-text tone="subdued">Unassigned</s-text>`;
          }
          const usersMap = ticketUsersMapSignal.value;
          const names = assignments.map(id => {
            const user = usersMap.get(id);
            if (!user) return 'Unknown';
            return user.email.split('@')[0];
          });
          return `<s-text style="font-size: 0.85rem;">${escapeHtml(names.join(', '))}</s-text>`;
        }
      },
      {
        key: 'createdAt',
        header: 'Created',
        sortable: true,
        sortType: 'date',
        render: (row: any) => formatDate(row.createdAt),
      },
      {
        key: '_actions',
        header: '',
        render: (row: any) =>
          `<s-button size="slim" variant="plain" data-action="view" data-id="${row.id}">View</s-button>`,
      },
    ];

    // Set initial items
    table.items = [...getFilteredTickets()];

    table.filterFunction = (item: any, query: string) => {
      const q = query.toLowerCase();
      const cn = getCustomerName(item.customerId).toLowerCase();
      return (
        (item.ticketNumber || '').toLowerCase().includes(q) ||
        cn.includes(q) ||
        (item.description || '').toLowerCase().includes(q) ||
        (item.contactNumber || '').toLowerCase().includes(q)
      );
    };

    // Row action handler for selecting a ticket
    table.addEventListener('row-action', (e: CustomEvent) => {
      const { action, id } = e.detail;
      if (action === 'view') {
        selectedTicketIdSignal.value = id;
        ticketSessionsSignal.value = [];
        ticketTimelineEventsSignal.value = [];
        // fetchTicketSessions owns the reRenderDetailPanel call on completion
        fetchTicketSessions(id);
      }
    });

    table.addEventListener('page-change', async (e: CustomEvent) => {
      ticketsPageSignal.value = e.detail.page;
      ticketsPageSizeSignal.value = e.detail.pageSize;
      await refreshTickets();
    });

    table.addEventListener('search-change', async (e: CustomEvent) => {
      ticketsSearchQuerySignal.value = e.detail.query || '';
      ticketsPageSignal.value = 1;
      await refreshTickets();
    });
  }

  // ── Local reactive effect: update table items when signals change ──
  // Dispose previous effect if page is re-mounted
  if (_ticketsEffectDisposer) {
    _ticketsEffectDisposer();
    _ticketsEffectDisposer = null;
  }
  _ticketsEffectDisposer = effect(() => {
    // Explicit signal subscriptions to ensure this local effect runs when they change
    ticketsListSignal.value;
    allCustomersSignal.value;
    ticketAssignmentsSignal.value;
    ticketUsersMapSignal.value;
    const totalCount = ticketsTotalCountSignal.value;
    ticketsStatusCountsSignal.value;
    const pageSize = ticketsPageSizeSignal.value;
    const page = ticketsPageSignal.value;
    const loading = ticketsLoadingSignal.value;

    const filtered = getFilteredTickets();
    const tbl = document.getElementById('tickets-data-table') as any;
    if (tbl) {
      tbl.items = [...filtered];
      tbl.totalCount = totalCount;
      tbl.setAttribute('page-size', String(pageSize));
      tbl.setAttribute('current-page', String(page));
    }

    // Toggle loading spinner visibility dynamically without rebuilding layout DOM
    const spinner = document.getElementById('tickets-loading-spinner');
    if (spinner) {
      spinner.style.display = loading ? 'flex' : 'none';
    }

    // Update total count badge content dynamically
    const totalBadge = document.getElementById('ticket-total-badge');
    if (totalBadge) {
      totalBadge.textContent = `${totalCount} total`;
    }

    // Also update filter tab counts + active states in-place
    updateFilterTabsInPlace();

    const selectedId = selectedTicketIdSignal.value;
    updateActiveRowHighlight(selectedId);
    requestAnimationFrame(() => {
      updateActiveRowHighlight(selectedTicketIdSignal.peek());
    });
  });

  // ── Restore scroll position and previously selected ticket from sessionStorage ──
  try {
    const savedScrollY = Number(sessionStorage.getItem('tickets_scroll_y') || '0');
    const savedSelectedId = sessionStorage.getItem('tickets_selected_id') || '';

    // Re-open detail panel for previously selected ticket
    if (savedSelectedId) {
      selectedTicketIdSignal.value = savedSelectedId;
      fetchTicketSessions(savedSelectedId);
      reRenderDetailPanel();
    }

    // Restore scroll after a brief paint cycle
    if (savedScrollY > 0) {
      requestAnimationFrame(() => {
        const wrapper = document.querySelector<HTMLElement>('.page-content-wrapper');
        if (wrapper) wrapper.scrollTop = savedScrollY;
      });
    }

    // Clear saved state so next fresh visit starts clean
    sessionStorage.removeItem('tickets_scroll_y');
    sessionStorage.removeItem('tickets_selected_id');
  } catch (e) {
    // sessionStorage unavailable
  }

  // Filter tab listeners — update signal, reset page, and fetch from API
  const filterTabs = document.querySelectorAll('.ticket-filter-tab');
  filterTabs.forEach(tab => {
    tab.addEventListener('click', async () => {
      const filter = (tab as HTMLElement).dataset.filter || 'all';
      ticketsStatusFilterSignal.value = filter;
      ticketsSearchQuerySignal.value = '';
      const table = document.getElementById('tickets-data-table') as any;
      if (table) {
        table.searchQuery = '';
      }
      ticketsPageSignal.value = 1;
      selectedTicketIdSignal.value = null;
      // Detail panel clears
      const wrapper = document.getElementById('ticket-detail-wrapper');
      if (wrapper) {
        const mobileHeader = wrapper.querySelector('.ticket-detail-mobile-header');
        const mobileHeaderHtml = mobileHeader ? mobileHeader.outerHTML : '';
        wrapper.innerHTML = mobileHeaderHtml + renderEmptyDetailPanel();
        wrapper.classList.remove('detail-visible');
      }
      await refreshTickets();
    });
  });

  // Mobile back button
  const closeDetailBtn = document.getElementById('btn-close-ticket-detail');
  if (closeDetailBtn) {
    closeDetailBtn.addEventListener('click', () => {
      selectedTicketIdSignal.value = null;
      const wrapper = document.getElementById('ticket-detail-wrapper');
      if (wrapper) wrapper.classList.remove('detail-visible');
    });
  }

  // Assign modal open
  const openAssignBtn = document.getElementById('btn-open-assign-modal');
  if (openAssignBtn) {
    openAssignBtn.addEventListener('click', () => { void hydrateAssignTechniciansModal(); });
  }

  // Initial binding of assign submit
  bindAssignSubmitListener();

  // Set up ticket intake listeners for modal
  setupIntakeListeners();
  openIntakeForMachineHistoryRoute();

  // Selection change handler & batch operations configuration
  let selectedTicketIds: string[] = [];
  if (table) {
    table.addEventListener('selection-change', (e: CustomEvent) => {
      selectedTicketIds = e.detail.selectedIds;

      const batchBar = document.getElementById('tickets-batch-bar');
      const selectedCountSpan = document.getElementById('batch-selected-count');
      if (batchBar) {
        if (selectedTicketIds.length > 0) {
          batchBar.style.display = 'flex';
          if (selectedCountSpan) selectedCountSpan.textContent = String(selectedTicketIds.length);
        } else {
          batchBar.style.display = 'none';
        }
      }
    });
  }

  const batchAssignBtn = document.getElementById('btn-batch-assign');
  if (batchAssignBtn) {
    batchAssignBtn.addEventListener('click', async () => {
      await refreshAssignableUsers(getAuthHeaders());

      const batchAssignModalOuter = document.getElementById('batch-assign-modal-outer-container');
      if (batchAssignModalOuter) {
        batchAssignModalOuter.innerHTML = renderBatchAssignModal();
        bindBatchAssignSubmitListener();
      }

      const modal = document.getElementById('batch-assign-techs-modal') as any;
      if (modal && typeof modal.showOverlay === 'function') {
        const errorEl = document.getElementById('batch-assign-techs-error');
        if (errorEl) errorEl.style.display = 'none';

        // Reset modal checkboxes
        const usersMap = ticketUsersMapSignal.value;
        usersMap.forEach((user, id) => {
          if (user.role === 'technician') {
            const cb = document.querySelector(`[name="batch-assign-tech-${id}"]`) as any;
            if (cb) cb.checked = false;
          }
        });

        modal.showOverlay();
      }
    });
  }

  const bindBatchAssignSubmitListener = () => {
    const submitBatchAssignBtn = document.getElementById('btn-submit-batch-assign');
    if (!submitBatchAssignBtn) return;

    submitBatchAssignBtn.addEventListener('click', async () => {
      if (selectedTicketIds.length === 0) return;

      const usersMap = ticketUsersMapSignal.value;
      const selectedTechIds: string[] = [];
      usersMap.forEach((user, id) => {
        if (user.role === 'technician') {
          const cb = document.querySelector(`[name="batch-assign-tech-${id}"]`) as any;
          if (cb && cb.checked) {
            selectedTechIds.push(id);
          }
        }
      });

      const errorEl = document.getElementById('batch-assign-techs-error');
      if (errorEl) errorEl.style.display = 'none';

      submitBatchAssignBtn.setAttribute('loading', 'true');
      submitBatchAssignBtn.setAttribute('disabled', 'true');

      let successCount = 0;
      let failCount = 0;

      for (const ticketId of selectedTicketIds) {
        try {
          const res = await fetch(`/api/v1/tickets/${ticketId}/assign`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ userIds: selectedTechIds })
          });
          if (res.ok) {
            successCount++;
            const updated = new Map(ticketAssignmentsSignal.value);
            updated.set(ticketId, selectedTechIds);
            ticketAssignmentsSignal.value = updated;
          } else {
            failCount++;
          }
        } catch (e) {
          failCount++;
        }
      }

      submitBatchAssignBtn.removeAttribute('loading');
      submitBatchAssignBtn.removeAttribute('disabled');

      if (failCount > 0) {
        if (errorEl) {
          errorEl.style.display = 'block';
          errorEl.textContent = `Assigned ${successCount} ticket(s). Failed for ${failCount} ticket(s).`;
        }
      } else {
        showToast(`Assigned technicians to ${successCount} ticket(s).`, 'success');
        const modal = document.getElementById('batch-assign-techs-modal') as any;
        if (modal && typeof modal.hideOverlay === 'function') modal.hideOverlay();
        if (table) table.clearSelection();
        reRenderDetailPanel();
      }
    });
  };

  bindBatchAssignSubmitListener();

  const batchCloseBtn = document.getElementById('btn-batch-close');
  if (batchCloseBtn) {
    batchCloseBtn.addEventListener('click', async () => {
      if (selectedTicketIds.length === 0) return;
      if (!confirm(`Are you sure you want to close the ${selectedTicketIds.length} selected tickets?`)) return;

      batchCloseBtn.setAttribute('loading', 'true');
      batchCloseBtn.setAttribute('disabled', 'true');

      let successCount = 0;
      let failCount = 0;

      for (const ticketId of selectedTicketIds) {
        try {
          const res = await fetch(`/api/v1/tickets/${ticketId}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ status: 'closed' })
          });
          if (res.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (e) {
          failCount++;
        }
      }

      batchCloseBtn.removeAttribute('loading');
      batchCloseBtn.removeAttribute('disabled');

      if (successCount > 0) {
        showToast(`${successCount} ticket(s) resolved successfully.`, 'success');
      }
      if (failCount > 0) {
        showToast(`Failed to resolve ${failCount} ticket(s).`, 'error');
      }

      await refreshTickets();
      if (table) table.clearSelection();
    });
  }

  // ── Sync Polling (Visibility Guarded) ──
  if (_ticketsPollInterval) {
    clearInterval(_ticketsPollInterval);
  }
  _ticketsPollInterval = setInterval(() => {
    // Skip polling if any modal is currently open to prevent UI/form state resets
    const isModalOpen = ['assign-techs-modal', 'batch-assign-techs-modal', 'intake-ticket-modal', 'reopen-ticket-modal']
      .some(id => {
        const m = document.getElementById(id);
        return m && m.hasAttribute('open');
      });

    if (document.visibilityState === 'visible' && !ticketsLoadingSignal.value && !isModalOpen) {
      refreshTickets();
    }
  }, 60000);

  // ── Keyboard Shortcuts (H5) ──
  const handleKeyDown = (e: KeyboardEvent) => {
    const activeEl = document.activeElement;
    if (
      activeEl &&
      (activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'TEXTAREA' ||
        activeEl.tagName === 'SELECT' ||
        activeEl.closest('s-text-field') ||
        activeEl.closest('s-text-area'))
    ) {
      if (e.key === 'Escape') {
        const modals = ['assign-techs-modal', 'batch-assign-techs-modal', 'intake-ticket-modal', 'reopen-ticket-modal'];
        modals.forEach(id => {
          const m = document.getElementById(id) as any;
          if (m && typeof m.hideOverlay === 'function') m.hideOverlay();
        });
      }
      return;
    }

    if (e.key === 'Escape') {
      const modals = ['assign-techs-modal', 'batch-assign-techs-modal', 'intake-ticket-modal', 'reopen-ticket-modal'];
      let modalClosed = false;
      modals.forEach(id => {
        const m = document.getElementById(id) as any;
        if (m && typeof m.hideOverlay === 'function' && m.hasAttribute('open')) {
          m.hideOverlay();
          modalClosed = true;
        }
      });

      if (!modalClosed) {
        const wrapperEl = document.getElementById('ticket-detail-wrapper') || document.querySelector('.ticket-detail-wrapper');
        if (wrapperEl && wrapperEl.classList.contains('fullscreen-mode')) {
          e.preventDefault();
          collapseTicketDetailPanel();
          return;
        }
      }
    }

    const tickets = getFilteredTickets();

    const currentId = selectedTicketIdSignal.peek();
    const currentIndex = currentId ? tickets.findIndex(t => t.id === currentId) : -1;

    if (e.key === 'j' || e.key === 'ArrowDown') {
      if (tickets.length === 0) return;
      e.preventDefault();
      const nextIndex = currentIndex + 1;
      if (nextIndex >= 0 && nextIndex < tickets.length) {
        const nextTicket = tickets[nextIndex];
        if (nextTicket && nextTicket.id) {
          const nextId = nextTicket.id;
          selectedTicketIdSignal.value = nextId;
          ticketSessionsSignal.value = [];
          ticketTimelineEventsSignal.value = [];
          fetchTicketSessions(nextId);
          updateActiveRowHighlight(nextId);

          setTimeout(() => {
            const rowBtn = document.querySelector(`[data-id="${nextId}"]`);
            if (rowBtn) {
              const rowEl = rowBtn.closest('s-table-row') || rowBtn;
              rowEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }, 50);
        }
      }
    } else if (e.key === 'k' || e.key === 'ArrowUp') {
      if (tickets.length === 0) return;
      e.preventDefault();
      const prevIndex = currentIndex - 1;
      if (prevIndex >= 0 && prevIndex < tickets.length) {
        const prevTicket = tickets[prevIndex];
        if (prevTicket && prevTicket.id) {
          const prevId = prevTicket.id;
          selectedTicketIdSignal.value = prevId;
          ticketSessionsSignal.value = [];
          ticketTimelineEventsSignal.value = [];
          fetchTicketSessions(prevId);
          updateActiveRowHighlight(prevId);

          setTimeout(() => {
            const rowBtn = document.querySelector(`[data-id="${prevId}"]`);
            if (rowBtn) {
              const rowEl = rowBtn.closest('s-table-row') || rowBtn;
              rowEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }, 50);
        }
      }
    } else if (e.key === 'h' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const currentFilter = ticketsStatusFilterSignal.peek();
      const idx = TICKET_FILTER_TAB_ORDER.indexOf(currentFilter);
      const prevIdx = idx >= 0 ? (idx - 1 + TICKET_FILTER_TAB_ORDER.length) % TICKET_FILTER_TAB_ORDER.length : TICKET_FILTER_TAB_ORDER.length - 1;
      const newFilter = TICKET_FILTER_TAB_ORDER[prevIdx];
      if (newFilter) switchFilterTab(newFilter);
    } else if (e.key === 'l' || e.key === 'ArrowRight') {
      e.preventDefault();
      const currentFilter = ticketsStatusFilterSignal.peek();
      const idx = TICKET_FILTER_TAB_ORDER.indexOf(currentFilter);
      const nextIdx = idx >= 0 ? (idx + 1) % TICKET_FILTER_TAB_ORDER.length : 0;
      const newFilter = TICKET_FILTER_TAB_ORDER[nextIdx];
      if (newFilter) switchFilterTab(newFilter);
    } else if (e.key === 'Enter') {
      const ticket = getSelectedTicket();
      if (ticket) {
        e.preventDefault();
        expandTicketDetailPanel();
      }
    } else if (e.key === 'c' || e.key === 'n') {
      const role = activeRoleSignal.peek();
      const canCreate = role !== 'viewer';
      if (canCreate) {
        e.preventDefault();
        const modal = document.getElementById('intake-ticket-modal') as any;
        if (modal && typeof modal.showOverlay === 'function') modal.showOverlay();
      }
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  _ticketsKeyDownHandler = handleKeyDown;
}

function openIntakeForMachineHistoryRoute(): void {
  const route = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const assetId = route.get('action') === 'create' ? route.get('assetId') : null;
  if (!assetId) return;
  resetIntakeForm();
  const autocomplete = document.getElementById('intake-existing-asset-autocomplete') as { items?: Array<{ id: string; label: string }>; selectedId?: string; dispatchEvent: (event: Event) => boolean } | null;
  const asset = (assetsListSignal.value || []).find(item => item.id === assetId);
  if (!autocomplete || !asset) return;
  const model = (deviceModelsSignal.value || []).find(item => item.id === asset.modelId);
  autocomplete.items = [{ id: asset.id, label: `${asset.serialNumber} (${model ? `${model.vendor} ${model.modelName}` : 'Unknown Model'})` }];
  autocomplete.selectedId = asset.id;
  autocomplete.dispatchEvent(new CustomEvent('select', { detail: { id: asset.id } }));
  const modal = document.getElementById('intake-ticket-modal') as { showOverlay?: () => void } | null;
  modal?.showOverlay?.();
}

export function expandTicketDetailPanel() {
  const wrapperEl = document.getElementById('ticket-detail-wrapper') || document.querySelector('.ticket-detail-wrapper');
  if (!wrapperEl) return;

  wrapperEl.classList.add('fullscreen-mode');
  const label = document.getElementById('txt-ticket-fullscreen-label');
  const svg = document.getElementById('svg-ticket-fullscreen-icon');
  if (label) label.textContent = 'Collapse';
  if (svg) {
    svg.innerHTML = `<path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>`;
  }
}

export function collapseTicketDetailPanel() {
  const wrapperEl = document.getElementById('ticket-detail-wrapper') || document.querySelector('.ticket-detail-wrapper');
  if (!wrapperEl) return;

  wrapperEl.classList.remove('fullscreen-mode');
  const label = document.getElementById('txt-ticket-fullscreen-label');
  const svg = document.getElementById('svg-ticket-fullscreen-icon');
  if (label) label.textContent = 'Expand';
  if (svg) {
    svg.innerHTML = `<path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>`;
  }
}

export function toggleTicketDetailPanel() {
  const wrapperEl = document.getElementById('ticket-detail-wrapper') || document.querySelector('.ticket-detail-wrapper');
  if (!wrapperEl) return;

  if (wrapperEl.classList.contains('fullscreen-mode')) {
    collapseTicketDetailPanel();
  } else {
    expandTicketDetailPanel();
  }
}

export function updateActiveRowHighlight(selectedId: string | null) {
  const table = document.getElementById('tickets-data-table') as any;
  if (!table) return;

  if ('selectedId' in table) {
    table.selectedId = selectedId;
  }

  const rows = table.querySelectorAll('s-table-row');
  rows.forEach((row: HTMLElement) => {
    const rowBtn = row.querySelector('[data-id]');
    const rowId = row.getAttribute('data-row-id') || (rowBtn ? rowBtn.getAttribute('data-id') : null);
    const isSelected = Boolean(selectedId && rowId === selectedId);

    if (isSelected) {
      row.classList.add('ticket-row-active');
      row.setAttribute('data-selected', 'true');
    } else {
      row.classList.remove('ticket-row-active');
      row.removeAttribute('data-selected');
    }

    // 1. Clear any display overrides on row.shadowRoot to preserve Polaris layout grid
    if (row.shadowRoot) {
      const styleEl = row.shadowRoot.querySelector('#active-row-style');
      if (styleEl) styleEl.textContent = '';
    }

    // 2. Apply inset box-shadow and background tint to child s-table-cell elements & shadowRoots
    const cells = row.querySelectorAll<HTMLElement>('s-table-cell');
    cells.forEach((cell: HTMLElement, idx: number) => {
      const isFirst = idx === 0;
      const isLast = idx === cells.length - 1;

      let shadowValue = '';
      if (isSelected) {
        const topShadow = 'inset 0 2px 0 0 #2b82fb';
        const bottomShadow = 'inset 0 -2px 0 0 #2b82fb';
        const leftShadow = isFirst ? 'inset 2px 0 0 0 #2b82fb' : '';
        const rightShadow = isLast ? 'inset -2px 0 0 0 #2b82fb' : '';
        shadowValue = [topShadow, bottomShadow, leftShadow, rightShadow].filter(Boolean).join(', ');
      }

      if (isSelected) {
        cell.style.setProperty('--p-color-bg-surface', '#f0f7ff');
        cell.style.setProperty('background-color', '#f0f7ff', 'important');
        cell.style.setProperty('box-shadow', shadowValue, 'important');
        cell.style.setProperty('transition', 'background-color 0.2s ease, box-shadow 0.2s ease', 'important');
      } else {
        cell.style.removeProperty('--p-color-bg-surface');
        cell.style.removeProperty('background-color');
        cell.style.removeProperty('box-shadow');
      }

      if (cell.shadowRoot) {
        let cellStyleEl = cell.shadowRoot.querySelector('#active-cell-style');
        if (!cellStyleEl) {
          cellStyleEl = document.createElement('style');
          cellStyleEl.id = 'active-cell-style';
          cell.shadowRoot.appendChild(cellStyleEl);
        }
        cellStyleEl.textContent = isSelected ? `
          :host, td, div, .cell, :host > * {
            background-color: #f0f7ff !important;
            box-shadow: ${shadowValue} !important;
            transition: background-color 0.2s ease, box-shadow 0.2s ease !important;
          }
        ` : '';
      }
    });
  });
}

const TICKET_FILTER_TAB_ORDER = ['open', 'assigned', 'in_progress', 'resolved', 'closed', 'all'];

async function switchFilterTab(filterKey: string) {
  ticketsStatusFilterSignal.value = filterKey;
  ticketsSearchQuerySignal.value = '';
  ticketsPageSignal.value = 1;

  const filterBar = document.getElementById('ticket-filter-bar-container');
  if (filterBar) filterBar.innerHTML = renderFilterTabs();

  // Re-bind click handlers for the updated filter tabs
  const filterTabs = document.querySelectorAll('.ticket-filter-tab');
  filterTabs.forEach(tab => {
    tab.addEventListener('click', async () => {
      const filter = (tab as HTMLElement).dataset.filter || 'all';
      switchFilterTab(filter);
    });
  });

  await refreshTickets();

  // Automatically select first ticket in newly filtered view if available
  const tickets = getFilteredTickets();
  if (tickets.length > 0 && tickets[0] && tickets[0].id) {
    const firstId = tickets[0].id;
    selectedTicketIdSignal.value = firstId;
    ticketSessionsSignal.value = [];
    ticketTimelineEventsSignal.value = [];
    fetchTicketSessions(firstId);
    updateActiveRowHighlight(firstId);
  } else {
    selectedTicketIdSignal.value = null;
    ticketSessionsSignal.value = [];
    ticketTimelineEventsSignal.value = [];
    reRenderDetailPanel();
    updateActiveRowHighlight(null);
  }
}


// ─── Partial Re-render ─────────────────────────────────────────────────────────

function reRenderDetailPanel() {
  const wrapper = document.getElementById('ticket-detail-wrapper');
  if (!wrapper) return;

  // Capture current scroll position of the timeline container
  const scrollContainer = document.getElementById('ticket-detail-scroll-container');
  const savedScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

  // Keep the mobile back header, replace the panel content
  const mobileHeader = wrapper.querySelector('.ticket-detail-mobile-header');
  const mobileHeaderHtml = mobileHeader ? mobileHeader.outerHTML : '';

  wrapper.innerHTML = mobileHeaderHtml + renderDetailPanel();
  wrapper.classList.add('detail-visible');

  // Restore scroll position after DOM replacement
  const newScrollContainer = document.getElementById('ticket-detail-scroll-container');
  if (newScrollContainer && savedScrollTop > 0) {
    newScrollContainer.scrollTop = savedScrollTop;
  }

  // Re-bind assign modal opener
  const openAssignBtn = document.getElementById('btn-open-assign-modal');
  if (openAssignBtn) {
    openAssignBtn.addEventListener('click', () => { void hydrateAssignTechniciansModal(); });
  }

  // Toggle detail panel fullscreen mode
  const toggleFullscreenBtn = document.getElementById('btn-toggle-ticket-detail-fullscreen');
  if (toggleFullscreenBtn) {
    toggleFullscreenBtn.addEventListener('click', () => {
      const wrapperEl = document.getElementById('ticket-detail-wrapper') || document.querySelector('.ticket-detail-wrapper');
      if (wrapperEl) {
        const isFullscreen = wrapperEl.classList.toggle('fullscreen-mode');
        const label = document.getElementById('txt-ticket-fullscreen-label');
        const svg = document.getElementById('svg-ticket-fullscreen-icon');
        if (label) label.textContent = isFullscreen ? 'Collapse' : 'Expand';
        if (svg) {
          svg.innerHTML = isFullscreen
            ? `<path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>`
            : `<path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>`;
        }
      }
    });
  }

  // Re-bind mobile close
  const closeDetailBtn = document.getElementById('btn-close-ticket-detail');
  if (closeDetailBtn) {
    closeDetailBtn.addEventListener('click', () => {
      selectedTicketIdSignal.value = null;
      wrapper.classList.remove('detail-visible');
    });
  }

  // Re-bind console button click
  const consoleBtn = document.getElementById('btn-open-tech-console');
  if (consoleBtn) {
    consoleBtn.addEventListener('click', () => {
      const ticket = getSelectedTicket();
      if (ticket) {
        window.location.hash = `#ticket-console?id=${ticket.id}`;
      }
    });
  }

  // Re-bind reopen ticket button
  const reopenBtn = document.getElementById('btn-reopen-ticket');
  if (reopenBtn) {
    reopenBtn.addEventListener('click', () => {
      const modal = document.getElementById('reopen-ticket-modal') as any;
      if (modal && typeof modal.showOverlay === 'function') modal.showOverlay();
    });
  }

  // Bind reopen confirm button
  const confirmReopenBtn = document.getElementById('btn-confirm-reopen');
  if (confirmReopenBtn) {
    confirmReopenBtn.addEventListener('click', async () => {
      const ticket = getSelectedTicket();
      if (!ticket) return;

      const reasonInput = document.getElementById('reopen-reason-input') as any;
      const reason = reasonInput?.value?.trim() || '';
      const errorEl = document.getElementById('reopen-error');

      if (!reason) {
        if (errorEl) { errorEl.style.display = 'block'; errorEl.textContent = 'Please provide a reason for reopening.'; }
        return;
      }

      try {
        const assignedTechIds = ticketAssignmentsSignal.peek().get(ticket.id) || [];
        const hasTechs = assignedTechIds.length > 0;
        const targetStatus = hasTechs ? 'in_progress' : 'open';

        const res = await fetch(`/api/v1/tickets/${ticket.id}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({ status: targetStatus, reopenReason: reason }),
        });

        if (res.ok) {
          const modal = document.getElementById('reopen-ticket-modal') as any;
          if (modal && typeof modal.hideOverlay === 'function') modal.hideOverlay();
          if (reasonInput) reasonInput.value = '';
          if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }

          // Switch to target status filter so the reopened ticket is visible and reset pagination page
          ticketsStatusFilterSignal.value = targetStatus;
          ticketsPageSignal.value = 1;
          selectedTicketIdSignal.value = ticket.id;

          // Refresh data — the local effect will update the table
          await refreshTickets();
          // Re-fetch timeline to show the reopen event
          fetchTicketSessions(ticket.id);
        } else {
          const errBody = await res.json().catch(() => ({ error: 'Reopen failed' }));
          if (errorEl) { errorEl.style.display = 'block'; errorEl.textContent = errBody.error || 'Failed to reopen ticket.'; }
        }
      } catch (e) {
        console.error('Reopen request failed:', e);
        if (errorEl) { errorEl.style.display = 'block'; errorEl.textContent = 'Network error during reopen.'; }
      }
    });
  }

  // Close ticket button for manager/admin in detail panel
  const closeBtn = document.getElementById('btn-close-ticket-manager');
  if (closeBtn) {
    closeBtn.addEventListener('click', async () => {
      const ticket = getSelectedTicket();
      if (!ticket) return;

      if (!confirm(`Are you sure you want to close Ticket #${ticket.ticketNumber}? This action is irreversible.`)) {
        return;
      }

      closeBtn.setAttribute('loading', 'true');
      closeBtn.setAttribute('disabled', 'true');

      try {
        const res = await fetch(`/api/v1/tickets/${ticket.id}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({ status: 'closed' }),
        });

        if (res.ok) {
          showToast(`Ticket #${ticket.ticketNumber} has been closed.`, 'success');
          // Switch to Closed status filter and reset pagination page
          ticketsStatusFilterSignal.value = 'closed';
          ticketsPageSignal.value = 1;
          selectedTicketIdSignal.value = ticket.id;

          // Refresh data — the local effect will update the table
          await refreshTickets();
          // Re-fetch timeline to show the close event
          fetchTicketSessions(ticket.id);
        } else {
          const errBody = await res.json().catch(() => ({ error: 'Close failed' }));
          showToast(errBody.error || 'Failed to close ticket.', 'error');
        }
      } catch (e) {
        console.error('Close request failed:', e);
        showToast('Network error during close.', 'error');
      } finally {
        closeBtn.removeAttribute('loading');
        closeBtn.removeAttribute('disabled');
      }
    });
  }

  // Edit ticket button for manager/admin in detail panel
  const editBtn = document.getElementById('btn-edit-ticket-manager');
  if (editBtn) {
    editBtn.addEventListener('click', () => {
      const ticket = getSelectedTicket();
      if (ticket) {
        openEditTicketModal(ticket);
      }
    });
  }
}

function openEditTicketModal(ticket: Ticket) {
  const modal = document.getElementById('edit-ticket-modal') as any;
  if (!modal) return;

  isPrepopulatingEditAsset = true;
  try {
    // Reset fields & errors
    const errorEl = document.getElementById('edit-ticket-error');
    if (errorEl) errorEl.style.display = 'none';

    // Set local state variables
    editSelectedCustomerId = ticket.customerId;
    editSelectedSiteId = ticket.siteId || null;
    editSelectedAssetId = ticket.assetId || null;

    // Set created date (formatted for datetime-local input: YYYY-MM-DDTHH:MM)
    const createdAtInput = document.getElementById('edit-ticket-created-at') as HTMLInputElement | null;
    if (createdAtInput) {
      const date = new Date(ticket.createdAt);
      // Convert to timezone-accurate local string: YYYY-MM-DDTHH:MM
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      createdAtInput.value = `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    }

    // Set contact details
    const contactNumInput = document.getElementById('edit-ticket-contact-number') as any;
    if (contactNumInput) contactNumInput.value = ticket.contactNumber || '';
    const contactEmailInput = document.getElementById('edit-ticket-contact-email') as any;
    if (contactEmailInput) contactEmailInput.value = ticket.contactEmail || '';

    // Pre-populate customer autocomplete
    const customerAutocomplete = document.getElementById('edit-ticket-customer-autocomplete') as any;
    if (customerAutocomplete) {
      const customer = allCustomersSignal.value.find(c => c.id === ticket.customerId);
      if (customer) {
        customerAutocomplete.items = [{ id: customer.id, label: customer.name }];
        customerAutocomplete.selectedId = customer.id;
      } else {
        customerAutocomplete.clear?.();
      }
      customerAutocomplete.removeAttribute('disabled');
    }

    // Pre-populate site autocomplete
    const siteAutocomplete = document.getElementById('edit-ticket-site-autocomplete') as any;
    if (siteAutocomplete) {
      if (ticket.customerId) {
        const customerSites = (sitesSignal.value || []).filter(s => s.customerId === ticket.customerId);
        siteAutocomplete.items = customerSites.map(s => ({ id: s.id, label: s.name }));
      }
      if (ticket.siteId) {
        const site = (sitesSignal.value || []).find(s => s.id === ticket.siteId);
        if (site) {
          siteAutocomplete.selectedId = site.id;
        } else {
          siteAutocomplete.clear?.();
        }
      } else {
        siteAutocomplete.clear?.();
      }
      siteAutocomplete.removeAttribute('disabled');
    }

    // Pre-populate asset autocomplete
    const assetAutocomplete = document.getElementById('edit-ticket-asset-autocomplete') as any;
    const lockBanner = document.getElementById('edit-ticket-asset-lock-banner');
    if (assetAutocomplete) {
      if (ticket.assetId) {
        const asset = assetsListSignal.value.find(a => a.id === ticket.assetId);
        const models = deviceModelsSignal.value || [];
        const m = asset ? models.find(model => model.id === asset.modelId) : null;
        const modelLabel = m ? `${m.vendor} ${m.modelName}`.trim() : 'Unknown Model';
        const labelStr = asset ? `${asset.serialNumber} (${modelLabel})` : `Asset ${ticket.assetId.substring(0, 8)}…`;

        assetAutocomplete.items = [{ id: ticket.assetId, label: labelStr }];
        assetAutocomplete.selectedId = ticket.assetId;

        // Lock customer/site as asset is selected initially
        if (customerAutocomplete) customerAutocomplete.setAttribute('disabled', 'true');
        if (siteAutocomplete) siteAutocomplete.setAttribute('disabled', 'true');
        if (lockBanner) lockBanner.style.display = 'block';
      } else {
        assetAutocomplete.clear?.();
        if (lockBanner) lockBanner.style.display = 'none';
      }
    }

    const noAssetsWarning = document.getElementById('edit-ticket-no-assets-warning');
    if (noAssetsWarning) noAssetsWarning.style.display = 'none';
  } finally {
    isPrepopulatingEditAsset = false;
  }

  // Show modal overlay
  if (typeof modal.showOverlay === 'function') {
    modal.showOverlay();
  }
}
